"use client"

import Link from "next/link"
import Script from "next/script"
import { useSearchParams } from "next/navigation"
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Edit3,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Copy,
  Download,
  Gift,
  Globe2,
  KeyRound,
  LockKeyhole,
  Loader2,
  LogOut,
  Mail,
  Map as MapIcon,
  MessageCircle,
  PackageCheck,
  Phone,
  QrCode,
  ReceiptText,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Settings2,
  ShoppingBag,
  Signal,
  Smartphone,
  Ticket,
  TicketPercent,
  Trash2,
  UserRound,
  UsersRound,
  Wallet,
  WalletCards,
  type LucideIcon,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { FormEvent, ReactNode } from "react"
import {
  captureAppleIdentity,
  captureGoogleIdentity,
  isOAuthCancellation,
  type OAuthIdentity,
} from "@/modules/account/oauth-bind"
import {
  localizePathWithQuery,
  localizeWebAccountPath,
} from "@/lib/account-links"
import { formatMinorAmount, isFreeAmount } from "@/lib/format-money"
import {
  extractEsimSpecs,
  formatCoverageLabel,
  formatProviderName,
  formatQuotaLabel,
  getLocalizedDescription,
  getLocalizedNotice,
  summarizeNetwork,
} from "@/lib/esim-specs"
import {
  getAppStoreUrl,
  GOOGLE_PLAY_URL,
  getAppLandingHref,
} from "@/lib/app-links"
import { useAuth } from "@/modules/auth"
import { toAnalyticsErrorCode } from "@/lib/analytics/error-code"
import { trackYohoEvent } from "@/lib/analytics/track"
import { InstallationPanel } from "./installation/InstallationPanel"
import enAccount from "../../../../../public/locales/en/account.json"
import zhCNAccount from "../../../../../public/locales/zh-CN/account.json"
import zhTWAccount from "../../../../../public/locales/zh-TW/account.json"
import zhHKAccount from "../../../../../public/locales/zh-HK/account.json"
import esAccount from "../../../../../public/locales/es/account.json"
import esLAAccount from "../../../../../public/locales/es-LA/account.json"
import frFRAccount from "../../../../../public/locales/fr-FR/account.json"
import frCAAccount from "../../../../../public/locales/fr-CA/account.json"
import jaJPAccount from "../../../../../public/locales/ja-JP/account.json"
import koAccount from "../../../../../public/locales/ko/account.json"
import deDEAccount from "../../../../../public/locales/de-DE/account.json"
import itITAccount from "../../../../../public/locales/it-IT/account.json"
import nlNLAccount from "../../../../../public/locales/nl-NL/account.json"
import ptBRAccount from "../../../../../public/locales/pt-BR/account.json"
import ptPTAccount from "../../../../../public/locales/pt-PT/account.json"
import plPLAccount from "../../../../../public/locales/pl-PL/account.json"
import ruRUAccount from "../../../../../public/locales/ru-RU/account.json"
import trTRAccount from "../../../../../public/locales/tr-TR/account.json"
import viVNAccount from "../../../../../public/locales/vi-VN/account.json"
import idIDAccount from "../../../../../public/locales/id-ID/account.json"
import thTHAccount from "../../../../../public/locales/th-TH/account.json"
import arEGAccount from "../../../../../public/locales/ar-EG/account.json"
import arSAAccount from "../../../../../public/locales/ar-SA/account.json"

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (options: Record<string, unknown>) => void
          prompt: () => void
          renderButton: (
            element: HTMLElement,
            options: Record<string, unknown>
          ) => void
        }
      }
    }
    AppleID?: {
      auth?: {
        init: (options: Record<string, unknown>) => void
        signIn: () => Promise<any>
      }
    }
    turnstile?: {
      render: (
        element: string | HTMLElement,
        options: Record<string, unknown>
      ) => string
      execute: (widgetId: string) => void
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

export type LocaleProps = {
  lng: string
}

type PageProps = LocaleProps & {
  redirectTo?: string
}

type DetailProps = LocaleProps & {
  esimId: string
}

type ApiState<T> = {
  data: T | null
  error: string | null
  loading: boolean
}

type AuthRuntime = {
  visitorId: string
  integrityToken: string
}

const TURNSTILE_SITE_KEY = "0x4AAAAAABgrbrzEaAg-Pafh"
const AUTH_VISITOR_ID_KEY = "yoho_web_auth_visitor_id"

function createTurnstileErrorMessage(lng: string) {
  return accountText(
    lng,
    "Security verification failed. Refresh and try again."
  )
}

function createSendCodeErrorMessage(
  lng: string,
  type: "whatsapp" | "sms",
  message?: unknown
) {
  if (
    typeof message === "string" &&
    message.trim() &&
    !/^\d+$/.test(message.trim())
  ) {
    return message.trim()
  }
  return type === "whatsapp"
    ? accountText(
        lng,
        "Failed to send the WhatsApp code. Check that the phone number is active on WhatsApp and try again."
      )
    : accountText(lng, "Failed to send the SMS code. Try again later.")
}

function getOrCreateAuthVisitorId() {
  const existing = window.localStorage.getItem(AUTH_VISITOR_ID_KEY)
  if (existing) return existing
  const randomId =
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  const visitorId = `web_auth_${randomId}`
  window.localStorage.setItem(AUTH_VISITOR_ID_KEY, visitorId)
  return visitorId
}

function buildAuthVisitorPayload(visitorId: string) {
  return {
    status: "storedSame",
    data: {
      visitorId,
      deviceData: {
        version: 1,
        platform: "web",
        model: "Web Browser",
        screenWidth: String(window.screen?.width || 0),
        screenHeight: String(window.screen?.height || 0),
        identifierForVendor: window.navigator.userAgent,
        isPhysicalDevice: true,
        platformSpecificData: {
          userAgent: window.navigator.userAgent,
          language: window.navigator.language,
        },
      },
      version: 1,
    },
  }
}

async function loadAuthRuntime(locale: string): Promise<AuthRuntime> {
  const visitorId = getOrCreateAuthVisitorId()
  const response = await fetch("/api/store/initialize", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      "visitor-id": visitorId,
    },
    body: JSON.stringify({
      integrity: { isPass: true },
      locale,
      visitor: buildAuthVisitorPayload(visitorId),
    }),
  })
  const result = await response.json()
  if (!response.ok || result?.is_success === false) {
    throw new Error(result?.message || "initialize_auth_runtime_failed")
  }
  const integrityToken =
    result?.data?.integrityId || result?.data?.visitorId || visitorId
  return { visitorId: result?.data?.visitorId || visitorId, integrityToken }
}

export type AnyRecord = Record<string, any>

const COUNTRY_DIAL_OPTIONS = [
  { code: "CN", dialCode: "+86" },
  { code: "HK", dialCode: "+852" },
  { code: "MO", dialCode: "+853" },
  { code: "TW", dialCode: "+886" },
  { code: "SG", dialCode: "+65" },
  { code: "MY", dialCode: "+60" },
  { code: "ID", dialCode: "+62" },
  { code: "TH", dialCode: "+66" },
  { code: "JP", dialCode: "+81" },
  { code: "KR", dialCode: "+82" },
  { code: "US", dialCode: "+1" },
  { code: "CA", dialCode: "+1" },
  { code: "GB", dialCode: "+44" },
  { code: "AU", dialCode: "+61" },
  { code: "NZ", dialCode: "+64" },
  { code: "AF", dialCode: "+93" },
  { code: "AL", dialCode: "+355" },
  { code: "DZ", dialCode: "+213" },
  { code: "AD", dialCode: "+376" },
  { code: "AO", dialCode: "+244" },
  { code: "AR", dialCode: "+54" },
  { code: "AM", dialCode: "+374" },
  { code: "AT", dialCode: "+43" },
  { code: "AZ", dialCode: "+994" },
  { code: "BH", dialCode: "+973" },
  { code: "BD", dialCode: "+880" },
  { code: "BY", dialCode: "+375" },
  { code: "BE", dialCode: "+32" },
  { code: "BZ", dialCode: "+501" },
  { code: "BJ", dialCode: "+229" },
  { code: "BT", dialCode: "+975" },
  { code: "BO", dialCode: "+591" },
  { code: "BA", dialCode: "+387" },
  { code: "BW", dialCode: "+267" },
  { code: "BR", dialCode: "+55" },
  { code: "BN", dialCode: "+673" },
  { code: "BG", dialCode: "+359" },
  { code: "BF", dialCode: "+226" },
  { code: "BI", dialCode: "+257" },
  { code: "KH", dialCode: "+855" },
  { code: "CM", dialCode: "+237" },
  { code: "CV", dialCode: "+238" },
  { code: "CF", dialCode: "+236" },
  { code: "TD", dialCode: "+235" },
  { code: "CL", dialCode: "+56" },
  { code: "CO", dialCode: "+57" },
  { code: "KM", dialCode: "+269" },
  { code: "CG", dialCode: "+242" },
  { code: "CD", dialCode: "+243" },
  { code: "CR", dialCode: "+506" },
  { code: "CI", dialCode: "+225" },
  { code: "HR", dialCode: "+385" },
  { code: "CU", dialCode: "+53" },
  { code: "CY", dialCode: "+357" },
  { code: "CZ", dialCode: "+420" },
  { code: "DK", dialCode: "+45" },
  { code: "DJ", dialCode: "+253" },
  { code: "EC", dialCode: "+593" },
  { code: "EG", dialCode: "+20" },
  { code: "SV", dialCode: "+503" },
  { code: "GQ", dialCode: "+240" },
  { code: "ER", dialCode: "+291" },
  { code: "EE", dialCode: "+372" },
  { code: "ET", dialCode: "+251" },
  { code: "FJ", dialCode: "+679" },
  { code: "FI", dialCode: "+358" },
  { code: "FR", dialCode: "+33" },
  { code: "GA", dialCode: "+241" },
  { code: "GM", dialCode: "+220" },
  { code: "GE", dialCode: "+995" },
  { code: "DE", dialCode: "+49" },
  { code: "GH", dialCode: "+233" },
  { code: "GR", dialCode: "+30" },
  { code: "GT", dialCode: "+502" },
  { code: "GN", dialCode: "+224" },
  { code: "GY", dialCode: "+592" },
  { code: "HT", dialCode: "+509" },
  { code: "HN", dialCode: "+504" },
  { code: "HU", dialCode: "+36" },
  { code: "IS", dialCode: "+354" },
  { code: "IN", dialCode: "+91" },
  { code: "IR", dialCode: "+98" },
  { code: "IQ", dialCode: "+964" },
  { code: "IE", dialCode: "+353" },
  { code: "IL", dialCode: "+972" },
  { code: "IT", dialCode: "+39" },
  { code: "JO", dialCode: "+962" },
  { code: "KZ", dialCode: "+7" },
  { code: "KE", dialCode: "+254" },
  { code: "KW", dialCode: "+965" },
  { code: "KG", dialCode: "+996" },
  { code: "LA", dialCode: "+856" },
  { code: "LV", dialCode: "+371" },
  { code: "LB", dialCode: "+961" },
  { code: "LY", dialCode: "+218" },
  { code: "LI", dialCode: "+423" },
  { code: "LT", dialCode: "+370" },
  { code: "LU", dialCode: "+352" },
  { code: "MG", dialCode: "+261" },
  { code: "MW", dialCode: "+265" },
  { code: "MV", dialCode: "+960" },
  { code: "ML", dialCode: "+223" },
  { code: "MT", dialCode: "+356" },
  { code: "MR", dialCode: "+222" },
  { code: "MU", dialCode: "+230" },
  { code: "MX", dialCode: "+52" },
  { code: "MD", dialCode: "+373" },
  { code: "MC", dialCode: "+377" },
  { code: "MN", dialCode: "+976" },
  { code: "ME", dialCode: "+382" },
  { code: "MA", dialCode: "+212" },
  { code: "MZ", dialCode: "+258" },
  { code: "MM", dialCode: "+95" },
  { code: "NA", dialCode: "+264" },
  { code: "NP", dialCode: "+977" },
  { code: "NL", dialCode: "+31" },
  { code: "NI", dialCode: "+505" },
  { code: "NE", dialCode: "+227" },
  { code: "NG", dialCode: "+234" },
  { code: "MK", dialCode: "+389" },
  { code: "NO", dialCode: "+47" },
  { code: "OM", dialCode: "+968" },
  { code: "PK", dialCode: "+92" },
  { code: "PA", dialCode: "+507" },
  { code: "PY", dialCode: "+595" },
  { code: "PE", dialCode: "+51" },
  { code: "PH", dialCode: "+63" },
  { code: "PL", dialCode: "+48" },
  { code: "PT", dialCode: "+351" },
  { code: "QA", dialCode: "+974" },
  { code: "RO", dialCode: "+40" },
  { code: "RU", dialCode: "+7" },
  { code: "RW", dialCode: "+250" },
  { code: "SA", dialCode: "+966" },
  { code: "SN", dialCode: "+221" },
  { code: "RS", dialCode: "+381" },
  { code: "SK", dialCode: "+421" },
  { code: "SI", dialCode: "+386" },
  { code: "SO", dialCode: "+252" },
  { code: "ZA", dialCode: "+27" },
  { code: "ES", dialCode: "+34" },
  { code: "LK", dialCode: "+94" },
  { code: "SD", dialCode: "+249" },
  { code: "SE", dialCode: "+46" },
  { code: "CH", dialCode: "+41" },
  { code: "SY", dialCode: "+963" },
  { code: "TJ", dialCode: "+992" },
  { code: "TZ", dialCode: "+255" },
  { code: "TG", dialCode: "+228" },
  { code: "TN", dialCode: "+216" },
  { code: "TR", dialCode: "+90" },
  { code: "TM", dialCode: "+993" },
  { code: "UG", dialCode: "+256" },
  { code: "UA", dialCode: "+380" },
  { code: "AE", dialCode: "+971" },
  { code: "UY", dialCode: "+598" },
  { code: "UZ", dialCode: "+998" },
  { code: "VE", dialCode: "+58" },
  { code: "VN", dialCode: "+84" },
  { code: "YE", dialCode: "+967" },
  { code: "ZM", dialCode: "+260" },
  { code: "ZW", dialCode: "+263" },
] as const

type CountryDialCode = (typeof COUNTRY_DIAL_OPTIONS)[number]["code"]

function getCountryFlagEmoji(countryCode: string) {
  return countryCode
    .toUpperCase()
    .replace(/[A-Z]/g, (char) =>
      String.fromCodePoint(127397 + char.charCodeAt(0))
    )
}

function getDefaultDialCountry(locale: string): CountryDialCode {
  const normalized = locale.toLowerCase()
  if (normalized === "zh-hk") return "HK"
  if (normalized === "zh-hant-tw" || normalized === "zh-tw") return "TW"
  if (normalized.startsWith("zh")) return "CN"
  if (normalized.startsWith("ja")) return "JP"
  if (normalized.startsWith("ko")) return "KR"
  if (normalized.startsWith("vi")) return "VN"
  if (normalized.startsWith("th")) return "TH"
  if (normalized.startsWith("id")) return "ID"
  if (normalized.startsWith("ms")) return "MY"
  if (normalized.startsWith("hi") || normalized.startsWith("bn")) return "IN"
  if (normalized.startsWith("ar")) return "AE"
  if (normalized.startsWith("ru")) return "RU"
  if (normalized.startsWith("fr")) return "FR"
  if (normalized.startsWith("de")) return "DE"
  if (normalized.startsWith("it")) return "IT"
  if (normalized.startsWith("es")) return "ES"
  if (normalized.startsWith("pt")) return "PT"
  return "US"
}

function getCountryDisplayName(code: string, locale: string) {
  const display = new Intl.DisplayNames([locale], { type: "region" })
  const name = display.of(code)
  if (!name) {
    throw new Error(`Missing country display name: ${locale}/${code}`)
  }
  return name
}

function getSortedCountryDialOptions(locale: string) {
  const collator = new Intl.Collator(locale, {
    sensitivity: "base",
    numeric: true,
  })
  return [...COUNTRY_DIAL_OPTIONS].sort((left, right) => {
    const leftName = getCountryDisplayName(left.code, locale)
    const rightName = getCountryDisplayName(right.code, locale)
    const nameOrder = collator.compare(leftName, rightName)
    if (nameOrder !== 0) return nameOrder
    const dialOrder = collator.compare(left.dialCode, right.dialCode)
    if (dialOrder !== 0) return dialOrder
    return collator.compare(left.code, right.code)
  })
}

type AccountMessages = Record<string, string>
type AccountResource = Record<string, unknown>

function extractAccountMessages(resource: AccountResource): AccountMessages {
  return Object.fromEntries(
    Object.entries(resource).filter(([, value]) => typeof value === "string")
  ) as AccountMessages
}

const accountMessagesByLocale = {
  en: enAccount,
  "zh-CN": zhCNAccount,
  "zh-TW": zhTWAccount,
  "zh-HK": zhHKAccount,
  es: esAccount,
  "es-LA": esLAAccount,
  "fr-FR": frFRAccount,
  "fr-CA": frCAAccount,
  "ja-JP": jaJPAccount,
  ko: koAccount,
  "de-DE": deDEAccount,
  "it-IT": itITAccount,
  "nl-NL": nlNLAccount,
  "pt-BR": ptBRAccount,
  "pt-PT": ptPTAccount,
  "pl-PL": plPLAccount,
  "ru-RU": ruRUAccount,
  "tr-TR": trTRAccount,
  "vi-VN": viVNAccount,
  "id-ID": idIDAccount,
  "th-TH": thTHAccount,
  "ar-EG": arEGAccount,
  "ar-SA": arSAAccount,
} satisfies Record<string, AccountResource>

function getAccountMessages(lng: string): AccountMessages {
  const messages =
    accountMessagesByLocale[lng as keyof typeof accountMessagesByLocale]
  if (!messages) {
    if (typeof console !== "undefined") {
      console.warn(
        `[personal-web] no account namespace for "${lng}", falling back to en`
      )
    }
    return extractAccountMessages(accountMessagesByLocale.en)
  }
  return extractAccountMessages(messages)
}

function formatAccountMessage(
  template: string,
  values?: Record<string, string | number>
) {
  if (!values) return template
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = values[key]
    if (value === undefined || value === null) return match
    return String(value)
  })
}

export function accountText(
  lng: string,
  key: string,
  values?: Record<string, string | number>
) {
  const messages = getAccountMessages(lng)
  const template = messages[key]
  if (template === undefined) {
    // Single missing key shouldn't crash the page; fall back to en value or the key itself.
    if (typeof console !== "undefined") {
      console.warn(
        `[personal-web] missing account translation ${lng}/${key}, falling back to en`
      )
    }
    const enTemplate = enAccount[key as keyof typeof enAccount]
    return formatAccountMessage(
      typeof enTemplate === "string" ? enTemplate : key,
      values
    )
  }
  return formatAccountMessage(template, values)
}

function copyText(lng: string, key: string) {
  return accountText(lng, `copy.${key}`)
}

function t(
  lng: string,
  _zh: string,
  en: string,
  values?: Record<string, string | number>
) {
  return accountText(lng, en, values)
}

function getLoginHref(lng: string, redirectTo: string, hash?: string) {
  const href = localizePathWithQuery("/account/me", lng, { redirectTo })
  return hash ? `${href}#${hash}` : href
}

function getCurrentRedirect(lng: string, pathname: string) {
  return localizeWebAccountPath(pathname, lng)
}

function useRemoveSsrFallback(id: string, ready: boolean = true) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted || !ready) return
    document.getElementById(id)?.remove()
  }, [id, mounted, ready])

  return mounted && ready
}

async function fetchEapi<T = AnyRecord>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const response = await fetch(`/api/eapi/${path.replace(/^\/+/, "")}`, {
    cache: "no-store",
    credentials: "include",
    headers: {
      accept: "application/json",
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(init?.headers || {}),
    },
    ...init,
  })

  const data = (await response.json().catch(() => null)) as T | null

  if (!response.ok) {
    const message =
      data && typeof data === "object" && "message" in data
        ? String((data as AnyRecord).message)
        : `request_failed_${response.status}`
    throw new Error(message)
  }

  return (data || ({} as T)) as T
}

function useEapi<T = AnyRecord>(
  path: string | null,
  deps: unknown[] = []
): ApiState<T> & { reload: () => void } {
  const [reloadKey, setReloadKey] = useState(0)
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    error: null,
    loading: Boolean(path),
  })

  useEffect(() => {
    let cancelled = false

    if (!path) {
      setState({ data: null, error: null, loading: false })
      return () => {
        cancelled = true
      }
    }

    setState((current) => ({ ...current, error: null, loading: true }))
    fetchEapi<T>(path)
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false })
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            data: null,
            error: error instanceof Error ? error.message : "request_failed",
            loading: false,
          })
        }
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, reloadKey, ...deps])

  return {
    ...state,
    reload: () => setReloadKey((current) => current + 1),
  }
}

function normalizeList(payload: unknown, keys: string[]): AnyRecord[] {
  if (Array.isArray(payload)) return payload as AnyRecord[]
  if (!payload || typeof payload !== "object") return []

  const record = payload as AnyRecord
  for (const key of keys) {
    const value = record[key]
    if (Array.isArray(value)) return value as AnyRecord[]
    if (value && typeof value === "object") {
      const nested = normalizeList(value, keys)
      if (nested.length > 0) return nested
    }
  }

  if (record.data) return normalizeList(record.data, keys)
  if (record.result) return normalizeList(record.result, keys)
  return []
}

export function pickValue(record: unknown, keys: string[]) {
  if (!record || typeof record !== "object") return ""
  const source = record as AnyRecord
  for (const key of keys) {
    const value = source[key]
    if (value !== null && value !== undefined && `${value}`.trim()) {
      return `${value}`
    }
  }
  return ""
}

function firstObject(payload: unknown, keys: string[]) {
  if (!payload || typeof payload !== "object") return null
  const record = payload as AnyRecord
  for (const key of keys) {
    const value = record[key]
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as AnyRecord
    }
  }
  return record
}

function formatDate(value: unknown) {
  if (!value) return ""
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

function formatMoney(value: unknown, currency?: unknown) {
  let numericValue = value
  if (typeof value !== "number") {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return ""
    numericValue = numeric
  }
  const amount =
    Math.abs(numericValue as number) > 999
      ? (numericValue as number) / 100
      : (numericValue as number)
  return `${currency ? `${currency} ` : ""}${amount.toFixed(2)}`
}

function PageShell({
  lng,
  title,
  subtitle,
  children,
  backHref,
  backLabel,
}: LocaleProps & {
  title: string
  subtitle: string
  children: ReactNode
  backHref?: string
  backLabel?: string
}) {
  const resolvedBackHref =
    backHref || localizeWebAccountPath("/account", lng)
  const resolvedBackLabel = backLabel || accountText(lng, "Account")
  return (
    <section className="min-h-[calc(100vh-220px)] bg-white px-4 py-5 pb-20 text-stone-950 md:px-10 md:py-8">
      <div className="mx-auto grid max-w-[1120px] gap-5 md:gap-6">
        <header className="grid gap-2">
          <a
            href={resolvedBackHref}
            className="inline-flex min-h-11 w-fit items-center gap-2 rounded-full px-3 text-sm font-semibold text-slate-600 transition hover:bg-white hover:text-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {resolvedBackLabel}
          </a>
          <h1 className="text-2xl font-semibold tracking-normal md:text-4xl">
            {title}
          </h1>
          <p className="max-w-3xl text-[15px] leading-6 text-slate-600 md:text-base md:leading-7">
            {subtitle}
          </p>
        </header>
        {children}
      </div>
    </section>
  )
}

function AccountCard({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={`rounded-[20px] border border-black/[0.06] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.04)] ${className}`}
    >
      {children}
    </section>
  )
}

function IconBox({
  icon: Icon,
  tone = "orange",
}: {
  icon: LucideIcon
  tone?: "orange" | "blue" | "green" | "slate" | "red" | "violet"
}) {
  const tones = {
    orange: "bg-orange-50 text-orange-600",
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    slate: "bg-slate-100 text-slate-700",
    red: "bg-rose-50 text-rose-600",
    violet: "bg-violet-50 text-violet-600",
  }

  return (
    <span
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${tones[tone]}`}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
  )
}

function SettingLink({
  icon,
  title,
  description,
  href,
  value,
  tone,
  onClick,
}: {
  icon: LucideIcon
  title: string
  description?: string
  href: string
  value?: string
  tone?: "orange" | "blue" | "green" | "slate" | "red" | "violet"
  onClick?: () => void
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="group flex min-h-[64px] items-center gap-4 rounded-2xl border border-transparent px-4 py-3 transition hover:border-orange-200 hover:bg-orange-50/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
    >
      <IconBox icon={icon} tone={tone} />
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold leading-6 text-slate-950">
          {title}
        </span>
        {description && (
          <span className="mt-1 block text-sm leading-5 text-slate-500">
            {description}
          </span>
        )}
      </span>
      {value && (
        <span className="max-w-[150px] truncate text-right text-sm font-semibold text-slate-500">
          {value}
        </span>
      )}
      <ChevronRight
        className="h-5 w-5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-orange-500"
        aria-hidden="true"
      />
    </a>
  )
}

function FreeEsimPathLink({
  icon,
  title,
  description,
  href,
  value,
  tone,
  disabled,
}: {
  icon: LucideIcon
  title: string
  description: string
  href: string
  value: string
  tone?: "orange" | "blue" | "green" | "slate" | "red" | "violet"
  disabled?: boolean
}) {
  const content = (
    <>
      <IconBox icon={icon} tone={tone} />
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold leading-6 text-slate-950">
          {title}
        </span>
        <span className="mt-1 block text-sm leading-5 text-slate-500">
          {description}
        </span>
      </span>
      <span className="max-w-[170px] truncate text-right text-sm font-semibold text-slate-500">
        {value}
      </span>
      <ChevronRight
        className="h-5 w-5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-orange-500"
        aria-hidden="true"
      />
    </>
  )

  const className =
    "group flex min-h-[64px] items-center gap-4 rounded-2xl border border-transparent px-4 py-3 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"

  if (disabled) {
    return (
      <div
        aria-disabled="true"
        className={`${className} cursor-wait opacity-70`}
      >
        {content}
      </div>
    )
  }

  return (
    <a
      href={href}
      className={`${className} hover:border-orange-200 hover:bg-orange-50/50`}
    >
      {content}
    </a>
  )
}

function PrimaryButton({
  children,
  type = "button",
  disabled,
  onClick,
}: {
  children: ReactNode
  type?: "button" | "submit"
  disabled?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex min-h-11 items-center justify-center rounded-full bg-orange-600 px-5 text-sm font-bold text-white transition hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  )
}

function SecondaryLink({
  href,
  children,
}: {
  href: string
  children: ReactNode
}) {
  return (
    <a
      href={href}
      className="inline-flex min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900 transition hover:border-orange-200 hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
    >
      {children}
    </a>
  )
}

function PrimaryActionLink({
  href,
  children,
}: {
  href: string
  children: ReactNode
}) {
  return (
    <a
      href={href}
      className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full bg-orange-600 px-5 text-sm font-bold text-white transition hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
    >
      {children}
    </a>
  )
}

function MutedLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="text-sm font-medium text-stone-500 transition-colors hover:text-orange-600"
    >
      {children}
    </a>
  )
}

type StatusTone = "orange" | "stone" | "green" | "red" | "blue"
type EsimStatusFilter = "all" | "active" | "notActive" | "terminated"

function normalizeStatus(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .trim()
}

function getEsimStatusFilter(status: string): Exclude<EsimStatusFilter, "all"> {
  const s = normalizeStatus(status)
  if (
    s.includes("terminated") ||
    s.includes("terminate") ||
    s.includes("cancel") ||
    s.includes("fail") ||
    s.includes("expired") ||
    s.includes("已终止") ||
    s.includes("已过期") ||
    s.includes("已取消") ||
    s.includes("error")
  ) {
    return "terminated"
  }
  if (
    s.includes("not active") ||
    s.includes("inactive") ||
    s.includes("pending") ||
    s.includes("not install") ||
    s.includes("not installed") ||
    s.includes("待激活") ||
    s.includes("未激活")
  ) {
    return "notActive"
  }
  return "active"
}

function detectStatusTone(status: string): StatusTone {
  const category = getEsimStatusFilter(status)
  if (category === "notActive") return "blue"
  if (category === "terminated") return "red"
  return "orange"
}

function prettifyStatus(raw: string, lng?: string): string {
  if (!raw) return ""
  const normalized = normalizeStatus(raw)
  if (lng) {
    const messages = getAccountMessages(lng)
    const translated = messages[`status.${normalized}`]
    if (translated !== undefined) return translated
  }
  if (/[一-鿿]/.test(raw)) return raw
  return raw
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

function esimStatusFilterLabel(lng: string, filter: EsimStatusFilter) {
  const labels = {
    all: accountText(lng, "All"),
    active: accountText(lng, "Active"),
    notActive: accountText(lng, "Not Active"),
    terminated: accountText(lng, "Terminated"),
  }
  return labels[filter]
}

function prettifyOrderItemTitle(raw: string, lng: string): string {
  if (!raw) return ""
  const skuMatch = raw.match(/^C[_]{1,2}([A-Z]{2}(?:_[A-Z]{2})*)$/)
  if (!skuMatch) return raw
  const codes = skuMatch[1].split("_").filter(Boolean)
  if (codes.length === 1) {
    const country = getCountryDisplayName(codes[0], lng)
    return accountText(lng, "{{country}} plan", { country })
  }
  return accountText(lng, "{{count}}-country plan", { count: codes.length })
}

function StatusBadge({ status, lng }: { status?: string; lng?: string }) {
  if (!status) return null
  const tone = detectStatusTone(status)
  const display = prettifyStatus(status, lng)
  const tones: Record<StatusTone, string> = {
    orange: "bg-orange-50 text-orange-700 border border-orange-200",
    stone: "bg-stone-100 text-stone-700 border border-stone-200",
    green: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    red: "bg-rose-50 text-rose-700 border border-rose-200",
    blue: "bg-blue-50 text-blue-700 border border-blue-200",
  }
  const dotColors: Record<StatusTone, string> = {
    orange: "bg-orange-500",
    stone: "bg-stone-400",
    green: "bg-emerald-500",
    red: "bg-rose-500",
    blue: "bg-blue-500",
  }
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotColors[tone]}`}
        aria-hidden="true"
      />
      {display}
    </span>
  )
}

const COUNTRY_FLAG_OFFSET = 0x1f1e6 - 0x41
function countryToFlag(code: string): string {
  const c = code.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(c)) return ""
  return String.fromCodePoint(
    c.charCodeAt(0) + COUNTRY_FLAG_OFFSET,
    c.charCodeAt(1) + COUNTRY_FLAG_OFFSET
  )
}

function LoadingCard({ title }: { title?: string }) {
  return (
    <AccountCard className="p-4 md:p-5">
      <div className="grid gap-3" aria-busy="true">
        {title && (
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {title}
          </div>
        )}
        {[0, 1, 2].map((item) => (
          <div
            key={item}
            className="flex min-h-[64px] animate-pulse items-center gap-4 rounded-2xl bg-slate-50 px-4 py-3"
          >
            <div className="h-11 w-11 rounded-2xl bg-slate-200" />
            <div className="grid flex-1 gap-2">
              <div className="h-4 w-36 rounded-full bg-slate-200" />
              <div className="h-3 w-56 max-w-full rounded-full bg-slate-100" />
            </div>
          </div>
        ))}
      </div>
    </AccountCard>
  )
}

function ErrorCard({
  message,
  onRetry,
  lng,
}: {
  message: string
  onRetry?: () => void
  lng: string
}) {
  return (
    <AccountCard className="p-5">
      <div className="flex items-start gap-3 text-rose-700">
        <IconBox icon={AlertTriangle} tone="red" />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">
            {accountText(lng, "Request failed")}
          </h2>
          <p className="mt-1 break-words text-sm leading-6 text-slate-600">
            {message}
          </p>
          {onRetry && (
            <div className="mt-4">
              <PrimaryButton onClick={onRetry}>
                {copyText(lng, "retry")}
              </PrimaryButton>
            </div>
          )}
        </div>
      </div>
    </AccountCard>
  )
}

interface LoginFeature {
  icon: LucideIcon
  title: string
  description: string
}

function LoginRequired({
  lng,
  redirectTo,
  title,
  description,
  features,
  secondaryAction,
}: LocaleProps & {
  redirectTo: string
  title?: string
  description?: string
  features?: LoginFeature[]
  secondaryAction?: { href: string; label: string }
}) {
  const loginHref = getLoginHref(lng, redirectTo)
  const hasFeatures = features && features.length > 0

  return (
    <div
      className={`grid gap-4 ${
        hasFeatures ? "lg:grid-cols-[minmax(0,1fr)_420px] lg:items-stretch" : ""
      }`}
    >
      <AccountCard className="p-5 md:p-7">
        <div className="flex h-full flex-col justify-between gap-8">
          <div className="flex flex-col gap-4">
            <IconBox icon={UserRound} tone="orange" />
            <div>
              <h2 className="text-[24px] font-semibold tracking-[-0.02em] text-slate-950 md:text-[30px]">
                {title || accountText(lng, "Sign in required")}
              </h2>
              <p className="mt-3 max-w-2xl text-[15px] leading-7 text-slate-600 md:text-base">
                {description ||
                  accountText(
                    lng,
                    "Sign in to continue managing your account information."
                  )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={loginHref}
              className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-orange-600 px-6 text-sm font-bold text-white transition hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
            >
              {copyText(lng, "loginButton")}
            </a>
            {secondaryAction && (
              <SecondaryLink href={secondaryAction.href}>
                {secondaryAction.label}
              </SecondaryLink>
            )}
          </div>
        </div>
      </AccountCard>

      {hasFeatures && (
        <AccountCard className="p-5 md:p-6">
          <h2 className="text-xl font-semibold tracking-[-0.01em] text-slate-950">
            {accountText(lng, "What can you do after signing in?")}
          </h2>
          <div className="mt-5 grid gap-2">
            {features!.map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-3 rounded-2xl border border-transparent p-3 transition hover:border-orange-100 hover:bg-orange-50/40"
              >
                <IconBox icon={feature.icon} tone="orange" />
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-950">
                    {feature.title}
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-slate-500">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </AccountCard>
      )}
    </div>
  )
}

function DataGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 md:grid-cols-2">{children}</div>
}

export function FieldRow({ label, value }: { label: string; value?: ReactNode }) {
  if (!value) return null
  return (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
      <dt className="text-xs font-semibold uppercase text-slate-500">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm font-semibold text-slate-950">
        {value}
      </dd>
    </div>
  )
}

function SectionTitle({
  icon,
  title,
  description,
}: {
  icon: LucideIcon
  title: string
  description?: string
}) {
  return (
    <div
      className={`flex gap-3 ${description ? "items-start" : "items-center"}`}
    >
      <IconBox icon={icon} tone="slate" />
      <div className="min-w-0 flex-1">
        <h2 className="text-lg font-semibold leading-tight text-slate-950">
          {title}
        </h2>
        {description && (
          <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
        )}
      </div>
    </div>
  )
}

export function AccountProfileWebPage({ lng, redirectTo }: PageProps) {
  const auth = useAuth()
  const isAuthResolved =
    auth.status === "authenticated" || auth.status === "unauthenticated"
  const ready = useRemoveSsrFallback("account-me-ssr-fallback", isAuthResolved)
  const finalRedirectTo = redirectTo || localizeWebAccountPath("/account", lng)

  if (!ready) {
    return null
  }

  // Three-state gate (was one big OR that conflated "no auth" with
  // "auth confirmed but customer object still in flight"). Splitting these
  // means we never render the login wall just because /api/auth/session
  // hasn't returned yet — layout.tsx already told us the cookie is good.
  if (auth.status === "unauthenticated") {
    return <WebLoginPanel lng={lng} redirectTo={finalRedirectTo} />
  }

  if (!auth.customer) {
    return (
      <PageShell
        lng={lng}
        title={accountText(lng, "My Account")}
        subtitle={accountText(lng, "Manage account and settings")}
      >
        <LoadingCard title={copyText(lng, "loading")} />
      </PageShell>
    )
  }

  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "My Account")}
      subtitle={accountText(lng, "Manage account and settings")}
    >
      <ProfilePanel lng={lng} />
    </PageShell>
  )
}

function WebLoginPanel({ lng, redirectTo }: PageProps) {
  const auth = useAuth()
  const [phone, setPhone] = useState("")
  const [code, setCode] = useState("")
  const [status, setStatus] = useState("")
  const [isError, setIsError] = useState(false)
  const [pending, setPending] = useState<string | null>(null)
  const [channel, setChannel] = useState<"whatsapp" | "sms">("whatsapp")
  const [dialCountry, setDialCountry] = useState<CountryDialCode>(() =>
    getDefaultDialCountry(lng)
  )
  const [authRuntime, setAuthRuntime] = useState<AuthRuntime | null>(null)
  const [turnstileScriptReady, setTurnstileScriptReady] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileLoadFailed, setTurnstileLoadFailed] = useState(false)
  const turnstileWidgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    trackYohoEvent("login_view", {
      login_surface: "account_web",
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    loadAuthRuntime(lng)
      .then((runtime) => {
        if (!cancelled) setAuthRuntime(runtime)
      })
      .catch((error) => {
        if (!cancelled) {
          setMessage(
            error instanceof Error
              ? error.message
              : "initialize_auth_runtime_failed",
            true
          )
        }
      })
    return () => {
      cancelled = true
    }
  }, [lng])

  useEffect(() => {
    if (window.turnstile) {
      setTurnstileLoadFailed(false)
      setTurnstileScriptReady(true)
      return
    }

    const intervalId = window.setInterval(() => {
      if (window.turnstile) {
        window.clearInterval(intervalId)
        setTurnstileLoadFailed(false)
        setTurnstileScriptReady(true)
      }
    }, 200)

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId)
      if (!window.turnstile) {
        setTurnstileLoadFailed(true)
        setMessage(
          accountText(
            lng,
            "Security verification failed to load. Refresh and try again."
          ),
          true
        )
      }
    }, 8000)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [lng])

  useEffect(() => {
    return () => {
      const widgetId = turnstileWidgetIdRef.current
      if (widgetId) window.turnstile?.remove(widgetId)
      turnstileWidgetIdRef.current = null
    }
  }, [])

  const renderTurnstile = useCallback(() => {
    if (
      !turnstileScriptReady ||
      !window.turnstile ||
      turnstileWidgetIdRef.current
    )
      return
    const container = document.getElementById("yoho-auth-turnstile")
    if (!container) {
      throw new Error(
        accountText(lng, "Security verification container is missing.")
      )
    }

    container.innerHTML = ""
    const widgetId = window.turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      size: "flexible",
      theme: "light",
      appearance: "interaction-only",
      action: "login",
      callback: (token: string) => {
        setTurnstileLoadFailed(false)
        setTurnstileToken(token)
      },
      "error-callback": () => {
        setTurnstileToken("")
        setTurnstileLoadFailed(true)
        setMessage(createTurnstileErrorMessage(lng), true)
        return true
      },
      "expired-callback": () => {
        setTurnstileToken("")
      },
      "timeout-callback": () => {
        setTurnstileToken("")
      },
    })
    if (!widgetId) {
      throw new Error(createTurnstileErrorMessage(lng))
    }
    turnstileWidgetIdRef.current = widgetId
  }, [lng, turnstileScriptReady])

  const resetTurnstileToken = useCallback(() => {
    const widgetId = turnstileWidgetIdRef.current
    setTurnstileToken("")
    if (widgetId) {
      window.turnstile?.reset(widgetId)
    }
  }, [])

  useEffect(() => {
    renderTurnstile()
  }, [renderTurnstile, turnstileScriptReady])

  const selectedDial =
    COUNTRY_DIAL_OPTIONS.find((o) => o.code === dialCountry) ??
    COUNTRY_DIAL_OPTIONS[0]
  const composedPhone = `${selectedDial.dialCode}${phone.replace(/^[+\s]+/, "").replace(/\s+/g, "")}`

  const setMessage = (message: string, error = false) => {
    setStatus(message)
    setIsError(error)
  }

  const sendCode = async (type: "whatsapp" | "sms") => {
    trackYohoEvent("login_method_select", {
      login_method: type,
      login_surface: "account_web",
    })
    if (!phone.trim()) {
      setMessage(accountText(lng, "Enter your phone number first."), true)
      return
    }
    setPending(`send-${type}`)
    setMessage(accountText(lng, "Running security verification..."))
    try {
      if (!authRuntime) {
        throw new Error(
          accountText(lng, "Sign-in environment is not ready yet.")
        )
      }
      const securityToken = turnstileToken
      setMessage(accountText(lng, "Sending verification code..."))
      const response = await fetch("/api/auth/sms/send", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
          "visitor-id": authRuntime.visitorId,
        },
        body: JSON.stringify({
          phone: composedPhone,
          type,
          locale: lng,
          t: securityToken,
          device_fingerprint: authRuntime.visitorId,
          integrity_token: authRuntime.integrityToken,
        }),
      })
      const result = await response.json().catch(() => null)
      if (!response.ok || result?.success === false) {
        throw new Error(createSendCodeErrorMessage(lng, type, result?.message))
      }
      setMessage(
        type === "whatsapp"
          ? accountText(lng, "WhatsApp code sent.")
          : accountText(lng, "SMS code sent.")
      )
      resetTurnstileToken()
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : accountText(lng, "Failed to send code."),
        true
      )
    } finally {
      setPending(null)
    }
  }

  const verifyCode = async (type: "whatsapp" | "sms") => {
    if (!phone.trim() || !code.trim()) {
      setMessage(
        accountText(lng, "Enter your phone number and verification code."),
        true
      )
      return
    }
    setPending(`verify-${type}`)
    setMessage(accountText(lng, "Running security verification..."))
    try {
      if (!authRuntime) {
        throw new Error(
          accountText(lng, "Sign-in environment is not ready yet.")
        )
      }
      const securityToken = turnstileToken
      setMessage(accountText(lng, "Signing in..."))
      const response = await fetch(`/api/auth/callback/${type}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
          "visitor-id": authRuntime.visitorId,
        },
        body: JSON.stringify({
          phone: composedPhone,
          code: code.trim(),
          type,
          is_token_login: true,
          retry_times: 0,
          is_for_binding: false,
          t: securityToken,
          device_fingerprint: authRuntime.visitorId,
          integrity_token: authRuntime.integrityToken,
        }),
      })
      const result = await response.json().catch(() => null)
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || "login_failed")
      }
      trackYohoEvent("login_code_verify_success", {
        login_method: type,
        login_surface: "account_web",
      })
      await auth.refresh()
      window.location.assign(
        redirectTo || localizeWebAccountPath("/account", lng)
      )
    } catch (error) {
      const errorCode = toAnalyticsErrorCode(error, "login_failed")
      trackYohoEvent("login_code_verify_failed", {
        login_method: type,
        login_surface: "account_web",
        error_code: errorCode,
        error_message_code: errorCode,
      })
      setMessage(
        error instanceof Error
          ? error.message
          : accountText(lng, "Sign-in failed."),
        true
      )
    } finally {
      setPending(null)
    }
  }

  const submitGoogle = useCallback(
    async (response: any) => {
      if (!response?.credential) {
        setMessage(accountText(lng, "Google sign-in was not completed."), true)
        return
      }
      setPending("google")
      setMessage(accountText(lng, "Completing Google sign-in..."))
      try {
        const payload = decodeGoogleCredential(response.credential)
        const authResponse = await fetch("/api/auth/callback/google", {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": "application/json",
            accept: "application/json",
          },
          body: JSON.stringify({
            id_token: response.credential,
            email: payload.email || "",
            display_name: payload.name || "",
            photo_url: payload.picture || "",
          }),
        })
        const result = await authResponse.json().catch(() => null)
        if (!authResponse.ok || result?.success === false) {
          throw new Error(result?.message || "google_login_failed")
        }
        trackYohoEvent("login_code_verify_success", {
          login_method: "google",
          login_surface: "account_web",
        })
        await auth.refresh()
        window.location.assign(
          redirectTo || localizeWebAccountPath("/account", lng)
        )
      } catch (error) {
        const errorCode = toAnalyticsErrorCode(error, "google_login_failed")
        trackYohoEvent("login_code_verify_failed", {
          login_method: "google",
          login_surface: "account_web",
          error_code: errorCode,
          error_message_code: errorCode,
        })
        setMessage(
          error instanceof Error
            ? error.message
            : accountText(lng, "Google sign-in failed."),
          true
        )
      } finally {
        setPending(null)
      }
    },
    [auth, lng, redirectTo]
  )

  useEffect(() => {
    const container = document.getElementById("yoho-web-google-button")
    if (!container) return

    let cancelled = false

    const initGoogle = (attempt = 0) => {
      if (cancelled) return
      if (!window.google?.accounts?.id) {
        if (attempt < 40) window.setTimeout(() => initGoogle(attempt + 1), 250)
        return
      }
      window.google.accounts.id.initialize({
        client_id:
          "281626020447-k6cm18mjalauo05jsg0v2orsmdb8r0c9.apps.googleusercontent.com",
        callback: submitGoogle,
        ux_mode: "popup",
      })
      if (!container.hasChildNodes()) {
        window.google.accounts.id.renderButton(container, {
          theme: "outline",
          size: "large",
          type: "standard",
          shape: "pill",
          width: Math.min(360, container.clientWidth || 320),
        })
      }
    }

    initGoogle()

    return () => {
      cancelled = true
    }
  }, [submitGoogle])

  const startApple = async () => {
    trackYohoEvent("login_method_select", {
      login_method: "apple",
      login_surface: "account_web",
    })
    if (!window.AppleID?.auth) {
      setMessage(accountText(lng, "Apple sign-in is still loading."), true)
      return
    }
    setPending("apple")
    try {
      window.AppleID.auth.init({
        clientId: "com.yohomobile.esim.web",
        scope: "name email",
        redirectURI: `${window.location.origin}/callbacks/sign_in_with_apple`,
        usePopup: true,
      })
      const appleResponse = await window.AppleID.auth.signIn()
      const formData = new FormData()
      formData.set("code", appleResponse?.authorization?.code || "")
      formData.set("id_token", appleResponse?.authorization?.id_token || "")
      formData.set("state", appleResponse?.authorization?.state || "")
      if (appleResponse?.user) {
        formData.set("user", JSON.stringify(appleResponse.user))
      }
      const response = await fetch("/api/auth/callback/apple", {
        method: "POST",
        credentials: "include",
        headers: { accept: "application/json" },
        body: formData,
      })
      const result = await response.json().catch(() => null)
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || "apple_login_failed")
      }
      trackYohoEvent("login_code_verify_success", {
        login_method: "apple",
        login_surface: "account_web",
      })
      await auth.refresh()
      window.location.assign(
        redirectTo || localizeWebAccountPath("/account", lng)
      )
    } catch (error) {
      const errorCode = toAnalyticsErrorCode(error, "apple_login_failed")
      trackYohoEvent("login_code_verify_failed", {
        login_method: "apple",
        login_surface: "account_web",
        error_code: errorCode,
        error_message_code: errorCode,
      })
      setMessage(
        error instanceof Error
          ? error.message
          : accountText(lng, "Apple sign-in failed."),
        true
      )
    } finally {
      setPending(null)
    }
  }

  const startGoogle = () => {
    trackYohoEvent("login_method_select", {
      login_method: "google",
      login_surface: "account_web",
    })
    if (!window.google?.accounts?.id) {
      setMessage(accountText(lng, "Google sign-in is still loading."), true)
      return
    }
    window.google.accounts.id.prompt()
  }

  const accountHref = localizeWebAccountPath("/account", lng)
  const channelLabel =
    channel === "whatsapp"
      ? accountText(lng, "WhatsApp")
      : accountText(lng, "SMS")
  const verifyButtonLabel = accountText(lng, "Verify and continue")
  const sendingKey = `send-${channel}`
  const verifyingKey = `verify-${channel}`
  const canSendCode =
    Boolean(authRuntime) &&
    turnstileScriptReady &&
    Boolean(turnstileToken) &&
    !turnstileLoadFailed
  const switchChannelLabel =
    channel === "whatsapp"
      ? accountText(lng, "Use SMS instead")
      : accountText(lng, "Use WhatsApp instead")

  const valuePoints = [
    {
      icon: Smartphone,
      title: accountText(lng, "Manage eSIMs"),
      description: accountText(
        lng,
        "View installation details, activation status, and next actions."
      ),
    },
    {
      icon: ReceiptText,
      title: accountText(lng, "View orders"),
      description: accountText(
        lng,
        "Track plans, payment records, and invoices."
      ),
    },
    {
      icon: Gift,
      title: accountText(lng, "Claim rewards"),
      description: accountText(
        lng,
        "Use free eSIMs, referral rewards, and redemption codes."
      ),
    },
  ]

  return (
    <section className="bg-white px-4 pb-24 pt-8 text-stone-950 md:px-8 md:pt-14">
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
      />
      <Script
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
        strategy="afterInteractive"
      />
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onLoad={() => {
          setTurnstileLoadFailed(false)
          setTurnstileScriptReady(true)
        }}
        onReady={() => {
          setTurnstileLoadFailed(false)
          setTurnstileScriptReady(true)
        }}
        onError={() => {
          setTurnstileLoadFailed(true)
          setMessage(
            accountText(
              lng,
              "Security verification failed to load. Refresh and try again."
            ),
            true
          )
        }}
      />

      <div className="mx-auto grid w-full max-w-[1120px] gap-8 lg:grid-cols-[minmax(0,1fr)_440px] lg:items-center lg:gap-12">
        <div className="grid gap-7 lg:pr-8">
          <div className="grid gap-5">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-[13px] font-bold text-orange-700">
              <UserRound className="h-4 w-4" aria-hidden="true" />
              {accountText(lng, "Yoho Mobile Account")}
            </span>
            <div className="grid gap-4">
              <h1 className="max-w-3xl text-[36px] font-bold leading-[1.08] tracking-[-0.04em] text-stone-950 md:text-[52px]">
                {accountText(lng, "Sign in to manage your Yoho Mobile account")}
              </h1>
              <p className="max-w-2xl text-[16px] leading-7 text-stone-600 md:text-[18px] md:leading-8">
                {accountText(
                  lng,
                  "View eSIMs, orders, rewards, and installation details from one clear web dashboard."
                )}
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            {valuePoints.map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border border-stone-200 bg-white p-4 shadow-[0_18px_50px_-34px_rgba(28,25,23,0.45)]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                  <item.icon className="h-[19px] w-[19px]" aria-hidden="true" />
                </span>
                <h2 className="mt-4 text-[16px] font-extrabold text-stone-950">
                  {item.title}
                </h2>
                <p className="mt-1 text-[13px] leading-5 text-stone-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[28px] border border-stone-200 bg-white p-5 shadow-[0_24px_70px_-42px_rgba(28,25,23,0.55)] md:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="text-[12px] font-bold uppercase tracking-[0.16em] text-orange-600">
                {accountText(lng, "Sign in")}
              </span>
              <h2 className="mt-2 text-[28px] font-bold leading-tight tracking-[-0.03em] text-stone-950">
                {accountText(lng, "Access your account")}
              </h2>
              <p className="mt-2 text-[14px] leading-6 text-stone-600">
                {accountText(lng, "Choose a method to continue.")}
              </p>
            </div>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
              <UserRound className="h-6 w-6" aria-hidden="true" />
            </span>
          </div>

          <div className="mt-6 grid gap-3">
            <OAuthButton
              provider="google"
              label={accountText(lng, "Continue with Google")}
              loading={pending === "google"}
              onClick={startGoogle}
            />
            <OAuthButton
              provider="apple"
              label={accountText(lng, "Continue with Apple")}
              loading={pending === "apple"}
              onClick={startApple}
            />
            <div
              id="yoho-web-google-button"
              aria-hidden="true"
              className="hidden"
            />
          </div>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-stone-200" />
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-stone-400">
              {accountText(lng, "OR PHONE")}
            </span>
            <span className="h-px flex-1 bg-stone-200" />
          </div>

          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              void verifyCode(channel)
            }}
          >
            <div className="flex gap-2">
              <CountryDialPicker
                selected={selectedDial}
                onSelect={setDialCountry}
                locale={lng}
              />
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder={accountText(lng, "Phone number")}
                className="min-h-[48px] flex-1 rounded-2xl border border-stone-200 bg-white px-4 text-[15px] text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                aria-label={copyText(lng, "phone")}
              />
            </div>
            <div className="space-y-2 empty:hidden">
              <div id="yoho-auth-turnstile" className="empty:hidden" />
              {turnstileLoadFailed && (
                <p className="text-[12px] font-medium text-stone-500">
                  {accountText(
                    lng,
                    "Security verification failed. Refresh and try again."
                  )}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={accountText(lng, "Verification code")}
                className="min-h-[48px] flex-1 rounded-2xl border border-stone-200 bg-white px-4 text-[15px] text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                aria-label={copyText(lng, "smsCode")}
              />
              <button
                type="button"
                onClick={() => void sendCode(channel)}
                disabled={pending === sendingKey || !canSendCode}
                className="min-h-[48px] shrink-0 rounded-2xl border border-stone-200 bg-white px-4 text-[13px] font-bold text-stone-700 transition hover:border-orange-300 hover:bg-orange-50/30 hover:text-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:opacity-60"
              >
                {pending === sendingKey
                  ? accountText(lng, "Sending…")
                  : !canSendCode
                    ? !turnstileScriptReady
                      ? accountText(lng, "Loading security")
                      : accountText(lng, "Verify first")
                    : channel === "whatsapp"
                      ? accountText(lng, "via WhatsApp")
                      : accountText(lng, "via SMS")}
              </button>
            </div>

            <button
              type="submit"
              disabled={pending === verifyingKey}
              className="mt-2 inline-flex min-h-[50px] items-center justify-center rounded-2xl bg-orange-500 px-4 text-[15px] font-extrabold text-white shadow-[0_14px_34px_-18px_rgba(234,88,12,0.75)] transition hover:bg-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:opacity-60"
            >
              {pending === verifyingKey
                ? accountText(lng, "Signing in…")
                : verifyButtonLabel}
            </button>

            <p
              aria-live="polite"
              className={`min-h-5 text-center text-[13px] ${isError ? "text-rose-600" : "text-emerald-600"}`}
            >
              {status}
            </p>
          </form>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[13px]">
            <span className="text-stone-500">
              {accountText(lng, "Currently using")}
              <span className="ml-1 font-bold text-stone-700">
                {channelLabel}
              </span>
            </span>
            <span className="text-stone-300">·</span>
            <button
              type="button"
              onClick={() => {
                const nextChannel = channel === "whatsapp" ? "sms" : "whatsapp"
                trackYohoEvent("login_method_select", {
                  login_method: nextChannel,
                  login_surface: "account_web",
                })
                setChannel(nextChannel)
              }}
              className="font-bold text-orange-600 transition hover:text-orange-700"
            >
              {switchChannelLabel}
            </button>
          </div>

          <div className="mt-7 text-center">
            <a
              href={accountHref}
              className="text-[13px] font-semibold text-stone-500 transition hover:text-orange-600"
            >
              {accountText(lng, "← Back to Account")}
            </a>
          </div>

          <p className="mt-5 text-center text-[12px] leading-5 text-stone-400">
            {accountText(lng, "By signing in, you agree to Yoho Mobile's ")}
            <a
              href={localizeWebAccountPath("/terms-of-conditions", lng)}
              className="text-stone-500 underline-offset-2 hover:underline"
            >
              {accountText(lng, "Terms")}
            </a>
            {accountText(lng, " and ")}
            <a
              href={localizeWebAccountPath("/privacy-policy", lng)}
              className="text-stone-500 underline-offset-2 hover:underline"
            >
              {accountText(lng, "Privacy Policy")}
            </a>
            {accountText(lng, ".")}
          </p>
        </div>
      </div>
    </section>
  )
}
function OAuthButton({
  provider,
  label,
  loading,
  onClick,
}: {
  provider: "google" | "apple"
  label: string
  loading: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="group inline-flex min-h-[46px] w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 text-[14px] font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:opacity-60"
    >
      {provider === "google" ? <GoogleGlyph /> : <AppleGlyph />}
      <span>{loading ? "…" : label}</span>
    </button>
  )
}

function CountryDialPicker({
  selected,
  onSelect,
  locale,
}: {
  selected: { code: CountryDialCode; dialCode: string }
  onSelect: (code: CountryDialCode) => void
  locale: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handlePointer = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
      }
    }
    document.addEventListener("pointerdown", handlePointer)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("pointerdown", handlePointer)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open])

  const sortedOptions = useMemo(
    () => getSortedCountryDialOptions(locale),
    [locale]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sortedOptions
    return sortedOptions.filter((opt) => {
      const name = getCountryDisplayName(opt.code, locale).toLowerCase()
      return (
        opt.code.toLowerCase().includes(q) ||
        opt.dialCode.includes(q) ||
        name.includes(q)
      )
    })
  }, [query, locale, sortedOptions])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex min-h-[46px] items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-[14px] font-medium text-slate-800 transition hover:border-slate-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="text-base leading-none">
          {getCountryFlagEmoji(selected.code)}
        </span>
        <span className="font-semibold">{selected.dialCode}</span>
        <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-[calc(100%+6px)] z-30 w-[280px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_18px_40px_-12px_rgba(15,23,42,0.18)]"
        >
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={accountText(locale, "Search country or code")}
              className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-slate-400"
              autoFocus
            />
          </div>
          <ul className="max-h-[320px] overflow-y-auto py-1">
            {filtered.map((opt) => {
              const isSelected = opt.code === selected.code
              return (
                <li key={opt.code}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(opt.code)
                      setOpen(false)
                      setQuery("")
                    }}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left text-[14px] transition hover:bg-orange-50 ${
                      isSelected ? "text-orange-700" : "text-slate-800"
                    }`}
                  >
                    <span className="text-base leading-none">
                      {getCountryFlagEmoji(opt.code)}
                    </span>
                    <span className="flex-1 truncate">
                      {getCountryDisplayName(opt.code, locale)}
                    </span>
                    <span className="text-slate-500">{opt.dialCode}</span>
                    {isSelected && (
                      <Check
                        className="h-4 w-4 text-orange-600"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </li>
              )
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-[13px] text-slate-400">
                {accountText(locale, "No matches")}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.614z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.581C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  )
}

function AppleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      className="shrink-0 text-slate-900"
    >
      <path
        fill="currentColor"
        d="M14.94 6.04c-.1.07-1.78 1.03-1.78 3.13 0 2.43 2.13 3.29 2.2 3.31-.01.06-.34 1.18-1.13 2.32-.7 1.01-1.43 2.02-2.55 2.02-1.11 0-1.4-.65-2.69-.65-1.26 0-1.71.67-2.73.67s-1.73-.94-2.55-2.09C2.51 13.36 1.7 11.27 1.7 9.27c0-3.21 2.09-4.91 4.14-4.91 1.09 0 2 .73 2.7.73.66 0 1.68-.77 2.91-.77.46 0 2.18.04 3.49 1.72zM10.65 3.33c.51-.61.87-1.46.87-2.31 0-.12-.01-.24-.03-.34-.83.03-1.81.55-2.41 1.24-.47.53-.9 1.39-.9 2.25 0 .13.02.26.03.31.05.01.13.02.21.02.74 0 1.68-.5 2.23-1.17z"
      />
    </svg>
  )
}

function decodeGoogleCredential(token: string): AnyRecord {
  try {
    const payload = token.split(".")[1] || ""
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/")
    return JSON.parse(
      atob(normalized + "=".repeat((4 - (normalized.length % 4)) % 4))
    )
  } catch {
    return {}
  }
}

function getCustomerDisplayName(
  customer: NonNullable<ReturnType<typeof useAuth>["customer"]>
) {
  const fullName = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(" ")
    .trim()
  return (
    fullName || customer.email || customer.phone || customer.id || "Yoho Mobile"
  )
}

function getCustomerInitials(
  customer: NonNullable<ReturnType<typeof useAuth>["customer"]>
) {
  const source = customer.first_name || customer.email || customer.phone || "?"
  return source.trim().charAt(0).toUpperCase() || "?"
}

function ProfilePanel({ lng }: LocaleProps) {
  const auth = useAuth()
  const customer = auth.customer
  const [firstName, setFirstName] = useState(customer?.first_name || "")
  const [lastName, setLastName] = useState(customer?.last_name || "")
  const [email, setEmail] = useState(customer?.email || "")
  const [phone, setPhone] = useState(customer?.phone || "")
  const [status, setStatus] = useState("")
  const [saving, setSaving] = useState(false)
  const [editingProfile, setEditingProfile] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  if (!customer) {
    throw new Error("ProfilePanel requires authenticated customer")
  }

  const displayName = getCustomerDisplayName(customer)
  const primaryContact = customer.email || customer.phone || customer.id
  const phoneValue = customer.phone || accountText(lng, "Not linked")
  const emailValue = customer.email || accountText(lng, "Not linked")

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setStatus("")
    try {
      await fetchEapi("store/customers/me", {
        method: "POST",
        body: JSON.stringify({
          email,
          first_name: firstName,
          last_name: lastName,
          phone,
        }),
      })
      await auth.refresh()
      setEditingProfile(false)
      setStatus(accountText(lng, "Account details saved successfully."))
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "save_failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start">
      <AccountCard className="p-5 md:p-6 lg:sticky lg:top-24">
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-orange-50 text-2xl font-extrabold text-orange-600 ring-1 ring-orange-100">
              {getCustomerInitials(customer)}
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-[22px] font-extrabold tracking-[-0.02em] text-slate-950">
                {displayName}
              </h2>
              <p className="mt-1 truncate text-sm font-semibold text-slate-500">
                {primaryContact}
              </p>
            </div>
          </div>
          <div className="grid gap-2 rounded-2xl bg-slate-50 p-4 text-sm">
            <ProfileSummaryRow
              label={accountText(lng, "Email")}
              value={emailValue}
            />
            <ProfileSummaryRow
              label={accountText(lng, "Phone")}
              value={phoneValue}
            />
          </div>
          <p className="text-sm leading-6 text-slate-600">
            {accountText(
              lng,
              "Manage personal details, linked sign-in methods, and account security actions."
            )}
          </p>
        </div>
      </AccountCard>

      <div className="grid gap-5">
        <AccountCard className="p-5 md:p-6">
          <AccountSectionHeader
            icon={UserRound}
            title={accountText(lng, "Personal information")}
            description={accountText(
              lng,
              "Used for orders, invoices, and account identification."
            )}
          />
          {!editingProfile ? (
            <div className="mt-5 flex flex-col gap-4 rounded-3xl border border-slate-100 bg-white p-4 sm:flex-row sm:items-center">
              <IconBox icon={UserRound} tone="orange" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-extrabold text-slate-950">
                  {displayName}
                </p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-500">
                  {accountText(lng, "Name")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingProfile(true)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-orange-600 px-6 text-sm font-extrabold text-white transition hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
              >
                <Edit3 className="h-4 w-4" aria-hidden="true" />
                {accountText(lng, "Edit")}
              </button>
            </div>
          ) : (
            <form className="mt-5 grid gap-4" onSubmit={saveProfile}>
              <div className="grid gap-4 md:grid-cols-2">
                <ProfileInput
                  label={accountText(lng, "First name")}
                  value={firstName}
                  onChange={setFirstName}
                />
                <ProfileInput
                  label={accountText(lng, "Last name")}
                  value={lastName}
                  onChange={setLastName}
                />
                <ProfileInput
                  label={accountText(lng, "Email")}
                  value={email}
                  onChange={setEmail}
                  type="email"
                />
                <ProfileInput
                  label={accountText(lng, "Phone")}
                  value={phone}
                  onChange={setPhone}
                  type="tel"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? copyText(lng, "loading") : copyText(lng, "save")}
                </PrimaryButton>
                <button
                  type="button"
                  onClick={() => setEditingProfile(false)}
                  className="inline-flex min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                >
                  {accountText(lng, "Cancel")}
                </button>
              </div>
            </form>
          )}
          <p
            aria-live="polite"
            className="mt-3 min-h-5 text-sm font-semibold text-slate-600"
          >
            {status}
          </p>
        </AccountCard>

        <AccountCard className="overflow-hidden p-5 md:p-6">
          <AccountSectionHeader
            icon={ShieldCheck}
            title={accountText(lng, "Associated login")}
            description={accountText(
              lng,
              "Manage the methods you can use to sign in to Yoho Mobile."
            )}
          />
          <div className="mt-5 divide-y divide-slate-100 rounded-3xl border border-slate-100 bg-white">
            <AccountLoginMethodRow
              icon={Mail}
              title={accountText(lng, "Email")}
              value={emailValue}
              actionLabel={
                customer.email
                  ? accountText(lng, "Edit")
                  : accountText(lng, "Link")
              }
              actionTone="orange"
              onAction={() => setEditingProfile(true)}
            />
            <AccountLoginMethodRow
              icon={Phone}
              iconTone="green"
              title={accountText(lng, "Phone number")}
              value={phoneValue}
              actionLabel={
                customer.phone
                  ? accountText(lng, "Linked")
                  : accountText(lng, "Link")
              }
              actionTone={customer.phone ? "locked" : "orange"}
              onAction={() => setEditingProfile(true)}
            />
            <BindingRow lng={lng} provider="google" />
            <BindingRow lng={lng} provider="apple" />
          </div>
        </AccountCard>

        <AccountCard className="p-5 md:p-6">
          <AccountSectionHeader
            icon={LogOut}
            title={accountText(lng, "Account actions")}
            description={accountText(
              lng,
              "Sign out of Yoho Mobile in this browser."
            )}
          />
          <button
            type="button"
            onClick={() =>
              void auth.logout({
                redirectTo: localizeWebAccountPath("/account", lng),
              })
            }
            className="mt-5 flex w-full items-center gap-4 rounded-3xl border border-slate-100 bg-white p-4 text-left transition hover:border-orange-200 hover:bg-orange-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
          >
            <IconBox icon={LogOut} tone="slate" />
            <span className="min-w-0 flex-1">
              <span className="block text-base font-extrabold text-slate-950">
                {copyText(lng, "logout")}
              </span>
              <span className="mt-1 block text-sm leading-5 text-slate-500">
                {accountText(lng, "You can sign in again whenever needed.")}
              </span>
            </span>
            <ChevronRight
              className="h-5 w-5 shrink-0 text-slate-400"
              aria-hidden="true"
            />
          </button>
        </AccountCard>

        <AccountCard className="border-rose-100 p-5 md:p-6">
          <AccountSectionHeader
            icon={Trash2}
            iconTone="red"
            title={accountText(lng, "Account management")}
            description={accountText(
              lng,
              "Deleting your account permanently removes account data and cannot be undone."
            )}
          />
          <button
            type="button"
            onClick={() => setDeleteModalOpen(true)}
            className="mt-5 flex w-full items-center gap-4 rounded-3xl border border-rose-100 bg-white p-4 text-left transition hover:border-rose-200 hover:bg-rose-50/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500"
          >
            <IconBox icon={Trash2} tone="red" />
            <span className="min-w-0 flex-1">
              <span className="block text-base font-extrabold text-rose-600">
                {accountText(lng, "Delete account")}
              </span>
              <span className="mt-1 block text-sm leading-5 text-slate-500">
                {accountText(
                  lng,
                  "Permanently delete your account. This process cannot be undone."
                )}
              </span>
            </span>
            <ChevronRight
              className="h-5 w-5 shrink-0 text-rose-400"
              aria-hidden="true"
            />
          </button>
        </AccountCard>
      </div>
      {deleteModalOpen ? (
        <DeleteAccountModal
          lng={lng}
          customerPhone={customer.phone || ""}
          onClose={() => setDeleteModalOpen(false)}
          onEditProfile={() => {
            setDeleteModalOpen(false)
            setEditingProfile(true)
          }}
        />
      ) : null}
      {/* Provider SDKs are needed for binding (the WebLoginPanel sibling loads
          them too, but only when unauthenticated). Turnstile is needed by the
          delete-account OTP send. */}
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
      />
      <Script
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
        strategy="afterInteractive"
      />
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
      />
    </div>
  )
}

function ProfileSummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
        {label}
      </span>
      <span className="truncate text-sm font-bold text-slate-700">{value}</span>
    </div>
  )
}

function AccountSectionHeader({
  icon,
  iconTone = "orange",
  title,
  description,
}: {
  icon: LucideIcon
  iconTone?: "orange" | "blue" | "green" | "slate" | "red" | "violet"
  title: string
  description: string
}) {
  return (
    <div className="flex items-start gap-3">
      <IconBox icon={icon} tone={iconTone} />
      <div className="min-w-0">
        <h2 className="text-xl font-extrabold tracking-[-0.01em] text-slate-950">
          {title}
        </h2>
        <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
      </div>
    </div>
  )
}

function AccountLoginMethodRow({
  icon,
  customIcon,
  iconTone = "slate",
  title,
  value,
  actionLabel,
  actionTone,
  href,
  onAction,
}: {
  icon?: LucideIcon
  customIcon?: ReactNode
  iconTone?: "orange" | "blue" | "green" | "slate" | "red" | "violet"
  title: string
  value: string
  actionLabel: string
  actionTone: "orange" | "locked"
  href?: string
  onAction?: () => void
}) {
  const actionClassName =
    actionTone === "locked"
      ? "bg-slate-100 text-slate-500"
      : "bg-orange-600 text-white hover:bg-orange-700"
  const iconNode = customIcon ? (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-50">
      {customIcon}
    </span>
  ) : icon ? (
    <IconBox icon={icon} tone={iconTone} />
  ) : null
  const actionContent = (
    <span
      className={`inline-flex min-h-10 min-w-[92px] items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold transition ${actionClassName}`}
    >
      {actionTone === "locked" ? (
        <LockKeyhole className="h-4 w-4" aria-hidden="true" />
      ) : null}
      {actionLabel}
    </span>
  )

  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        {iconNode}
        <span className="min-w-0">
          <span className="block text-base font-extrabold text-slate-950">
            {title}
          </span>
          <span className="mt-1 block truncate text-sm font-semibold text-slate-500">
            {value}
          </span>
        </span>
      </div>
      {href ? (
        <a
          href={href}
          className="self-start rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 sm:self-center"
        >
          {actionContent}
        </a>
      ) : (
        <button
          type="button"
          onClick={onAction}
          className="self-start rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 sm:self-center"
        >
          {actionContent}
        </button>
      )}
    </div>
  )
}

type ProviderName = "google" | "apple"

// Metadata field names per Flutter bundle. Same shape on the proxy side.
function bindingFieldsFor(provider: ProviderName) {
  return provider === "google"
    ? { emailKey: "bind_google_email", idKey: "bind_google_id", legacyId: "google_id" }
    : { emailKey: "bind_apple_email", idKey: "bind_apple_id", legacyId: "apple_id" }
}

function readBindingFromMetadata(
  metadata: Record<string, unknown> | undefined,
  provider: ProviderName
) {
  if (!metadata) return { bound: false, email: "" }
  const fields = bindingFieldsFor(provider)
  const idValue = (metadata[fields.idKey] ?? metadata[fields.legacyId]) as
    | string
    | undefined
  const emailValue = metadata[fields.emailKey] as string | undefined
  const bound =
    typeof idValue === "string" && idValue.length > 0 && idValue !== "null"
  return { bound, email: typeof emailValue === "string" ? emailValue : "" }
}

function isApplePrivateRelay(email: string) {
  return /@privaterelay\.appleid\.com$/i.test(email)
}

function BindingRow({
  lng,
  provider,
}: {
  lng: string
  provider: ProviderName
}) {
  const auth = useAuth()
  const metadata = (auth.customer?.metadata || {}) as Record<string, unknown>
  const { bound, email: boundEmail } = readBindingFromMetadata(
    metadata,
    provider
  )
  const [pending, setPending] = useState<"link" | "unlink" | null>(null)
  const [status, setStatus] = useState<{
    kind: "info" | "error" | "conflict"
    message: string
  } | null>(null)
  const [confirmingUnlink, setConfirmingUnlink] = useState(false)

  const providerLabel =
    provider === "google"
      ? accountText(lng, "Google Account")
      : accountText(lng, "Apple Account")

  const handleLink = useCallback(async () => {
    setPending("link")
    setStatus(null)
    trackYohoEvent("account_binding_attempt", { provider, op: "link" })
    let identity: OAuthIdentity
    try {
      identity =
        provider === "google"
          ? await captureGoogleIdentity()
          : await captureAppleIdentity(
              `${window.location.origin}/callbacks/sign_in_with_apple`
            )
    } catch (error) {
      // Dismissals ("tap_outside", popup closed) — silent. Real failures get
      // translated to a friendly message.
      if (!isOAuthCancellation(error)) {
        const code = error instanceof Error ? error.message : "link_failed"
        setStatus({
          kind: "error",
          message: friendlyOAuthError(lng, provider, code, providerLabel),
        })
        trackYohoEvent("account_binding_failed", { provider, error_kind: code })
      }
      setPending(null)
      return
    }

    try {
      const response = await fetch(`/api/account/binding/${provider}`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: identity.email,
          social_id: identity.socialId,
        }),
      })
      const result = (await response.json().catch(() => null)) as {
        success?: boolean
        message?: string
        bound_to_user?: Record<string, unknown>
      } | null

      if (response.status === 409 && result?.bound_to_user) {
        setStatus({
          kind: "conflict",
          message: describeBoundUser(lng, result.bound_to_user, providerLabel),
        })
        trackYohoEvent("account_binding_conflict", { provider })
        setPending(null)
        return
      }

      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || "link_failed")
      }

      await auth.refresh()
      trackYohoEvent("account_binding_success", { provider, op: "link" })

      if (provider === "apple" && isApplePrivateRelay(identity.email)) {
        setStatus({
          kind: "info",
          message: accountText(lng, "account.binding.applePrivateRelayHint"),
        })
      } else {
        setStatus({
          kind: "info",
          message: accountText(lng, "account.binding.success", {
            provider: providerLabel,
          }),
        })
      }
    } catch (error) {
      const code = error instanceof Error ? error.message : "link_failed"
      trackYohoEvent("account_binding_failed", { provider, error_kind: code })
      setStatus({
        kind: "error",
        message: friendlyOAuthError(lng, provider, code, providerLabel),
      })
    } finally {
      setPending(null)
    }
  }, [auth, lng, provider, providerLabel])

  const handleUnlink = useCallback(async () => {
    setPending("unlink")
    setStatus(null)
    trackYohoEvent("account_binding_attempt", { provider, op: "unlink" })
    try {
      const response = await fetch(`/api/account/binding/${provider}`, {
        method: "DELETE",
        credentials: "include",
      })
      const result = (await response.json().catch(() => null)) as {
        success?: boolean
        message?: string
      } | null
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || "unlink_failed")
      }
      await auth.refresh()
      trackYohoEvent("account_binding_success", { provider, op: "unlink" })
      setStatus({
        kind: "info",
        message: accountText(lng, "account.binding.unlinkSuccess", {
          provider: providerLabel,
        }),
      })
      setConfirmingUnlink(false)
    } catch (error) {
      const code = error instanceof Error ? error.message : "unlink_failed"
      trackYohoEvent("account_binding_failed", { provider, error_kind: code })
      setStatus({
        kind: "error",
        message: friendlyOAuthError(lng, provider, code, providerLabel),
      })
    } finally {
      setPending(null)
    }
  }, [auth, lng, provider, providerLabel])

  const valueLabel = bound
    ? boundEmail
      ? accountText(lng, "account.binding.linkedAs", { value: boundEmail })
      : accountText(lng, "account.binding.linked")
    : accountText(lng, "Not linked")

  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-50">
          {provider === "google" ? <GoogleGlyph /> : <AppleGlyph />}
        </span>
        <span className="min-w-0">
          <span className="block text-base font-extrabold text-slate-950">
            {providerLabel}
          </span>
          <span
            className={`mt-1 block truncate text-sm font-semibold ${
              bound ? "text-emerald-600" : "text-slate-500"
            }`}
          >
            {valueLabel}
          </span>
          {status ? (
            <span
              role={status.kind === "error" ? "alert" : "status"}
              className={`mt-2 block text-xs font-semibold leading-5 ${
                status.kind === "error" || status.kind === "conflict"
                  ? "text-rose-600"
                  : "text-emerald-600"
              }`}
            >
              {status.message}
            </span>
          ) : null}
        </span>
      </div>
      {bound ? (
        confirmingUnlink ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={handleUnlink}
              disabled={pending !== null}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-rose-600 px-4 text-sm font-extrabold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {pending === "unlink" ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              {accountText(lng, "account.binding.confirmUnlinkCta")}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingUnlink(false)}
              disabled={pending !== null}
              className="inline-flex min-h-10 items-center justify-center rounded-full border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {accountText(lng, "Cancel")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setStatus(null)
              setConfirmingUnlink(true)
            }}
            disabled={pending !== null}
            className="self-start inline-flex min-h-10 min-w-[92px] items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-extrabold text-slate-700 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500 sm:self-center"
          >
            {accountText(lng, "account.binding.unlink")}
          </button>
        )
      ) : (
        <button
          type="button"
          onClick={handleLink}
          disabled={pending !== null}
          className="self-start inline-flex min-h-10 min-w-[92px] items-center justify-center gap-2 rounded-full bg-orange-600 px-4 text-sm font-extrabold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 sm:self-center"
        >
          {pending === "link" ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : null}
          {accountText(lng, "account.binding.link")}
        </button>
      )}
    </div>
  )
}

// Map raw OAuth / proxy error codes to friendly translated strings. Anything
// not in the map falls back to a generic "couldn't link {{provider}}" message
// so users never see internal codes like `apple_link_failed`.
function friendlyOAuthError(
  lng: string,
  provider: ProviderName,
  code: string,
  providerLabel: string
) {
  switch (code) {
    case "google_sdk_not_ready":
    case "apple_sdk_not_ready":
      return accountText(lng, "account.binding.errorSdkNotReady", {
        provider: providerLabel,
      })
    case "google_userinfo_failed":
    case "google_userinfo_unparseable":
    case "google_no_access_token":
    case "google_id_token_unparseable":
    case "apple_id_token_unparseable":
      return accountText(lng, "account.binding.errorIdentityIncomplete", {
        provider: providerLabel,
      })
    case "metadata_update_failed":
      return accountText(lng, "account.binding.errorSaveFailed", {
        provider: providerLabel,
      })
    case "unauthenticated":
      return accountText(lng, "account.binding.errorUnauthenticated")
    default:
      return provider === "google"
        ? accountText(lng, "account.binding.errorLink", {
            provider: providerLabel,
          })
        : accountText(lng, "account.binding.errorLink", {
            provider: providerLabel,
          })
  }
}

function friendlyDeleteError(lng: string, code: string) {
  switch (code) {
    case "missing_code":
    case "invalid_code":
    case "verifysms_failed":
      return accountText(lng, "account.delete.errorBadCode")
    case "no_phone_on_account":
      return accountText(lng, "account.delete.errorNoPhone")
    case "unauthenticated":
      return accountText(lng, "account.binding.errorUnauthenticated")
    case "initialize_auth_runtime_failed":
    case "auth_runtime_failed":
      return accountText(lng, "account.delete.errorRuntimeNotReady")
    case "TURNSTILE_TOKEN_INVALID":
    case "TURNSTILE_TOKEN_REQUIRED":
    case "CLIENT_VERIFICATION_REQUIRED":
      return accountText(lng, "account.delete.errorTurnstileInvalid")
    case "invalid_login_code_request":
    case "send_code_failed":
      return accountText(lng, "account.delete.errorSendFailed")
    case "remove_account_failed":
    case "delete_account_failed":
      return accountText(lng, "account.delete.errorDeleteFailed")
    default:
      // Server might pass through a localised string from upstream
      // (getClientMessage in /api/auth/sms/send returns one). If it's not a
      // bare identifier code (snake_case or SCREAMING_SNAKE_CASE), show it
      // as-is; otherwise fall back to generic so users never see raw codes.
      if (/^[A-Za-z][A-Za-z0-9_]+$/.test(code)) {
        return accountText(lng, "account.delete.errorGeneric")
      }
      return code
  }
}

function describeBoundUser(
  lng: string,
  user: Record<string, unknown>,
  providerLabel: string
) {
  const firstName = typeof user.first_name === "string" ? user.first_name : ""
  const lastName = typeof user.last_name === "string" ? user.last_name : ""
  const email = typeof user.email === "string" ? user.email : ""
  const phone = typeof user.phone === "string" ? user.phone : ""
  const display =
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    email ||
    (phone ? phone.replace(/.(?=.{4})/g, "•") : "") ||
    accountText(lng, "another account")
  return accountText(lng, "account.binding.conflictBody", {
    provider: providerLabel,
    user: display,
  })
}

type DeleteState =
  | "warn"
  | "needs_phone"
  | "otp_idle"
  | "otp_sending"
  | "otp_sent"
  | "deleting"
  | "done"
type DeleteOtpChannel = "whatsapp" | "sms"

const DELETE_OTP_CHANNELS: {
  value: DeleteOtpChannel
  icon: LucideIcon
  labelKey: string
}[] = [
  {
    value: "whatsapp",
    icon: MessageCircle,
    labelKey: "WhatsApp",
  },
  {
    value: "sms",
    icon: Phone,
    labelKey: "SMS",
  },
]

function getDeleteSendCodeLabel(lng: string, channel: DeleteOtpChannel) {
  return channel === "whatsapp"
    ? copyText(lng, "sendWhatsapp")
    : `${accountText(lng, "account.delete.sendCodeCta")} (${accountText(
        lng,
        "SMS"
      )})`
}

function getDeleteSwitchChannelLabel(lng: string, channel: DeleteOtpChannel) {
  return channel === "whatsapp"
    ? accountText(lng, "Use SMS instead")
    : accountText(lng, "Use WhatsApp instead")
}

function getDeleteSendErrorMessage(
  lng: string,
  channel: DeleteOtpChannel,
  code: string
) {
  if (
    code === "missing_turnstile" ||
    code === "TURNSTILE_TOKEN_INVALID" ||
    code === "turnstile_failed"
  ) {
    return friendlyDeleteError(lng, code)
  }
  if (
    code === "send_code_failed" ||
    code === "invalid_login_code_request" ||
    code === "Failed to send login code"
  ) {
    return createSendCodeErrorMessage(lng, channel)
  }
  return createSendCodeErrorMessage(lng, channel, code)
}

function DeleteAccountModal({
  lng,
  customerPhone,
  onClose,
  onEditProfile,
}: {
  lng: string
  customerPhone: string
  onClose: () => void
  onEditProfile: () => void
}) {
  const auth = useAuth()
  const initialState: DeleteState = customerPhone ? "warn" : "needs_phone"
  const [state, setState] = useState<DeleteState>(initialState)
  const [channel, setChannel] = useState<DeleteOtpChannel>("whatsapp")
  const [code, setCode] = useState("")
  const [error, setError] = useState("")
  const [resendCountdown, setResendCountdown] = useState(0)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [authRuntime, setAuthRuntime] = useState<AuthRuntime | null>(null)
  const otpInputRef = useRef<HTMLInputElement | null>(null)
  const turnstileWidgetIdRef = useRef<string | null>(null)
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    trackYohoEvent("account_delete_view", { eligible: !!customerPhone })
  }, [customerPhone])

  // Upstream sendsms validates a device_fingerprint + integrity_token issued
  // by /api/store/initialize. Without it we get a 400 "设备标识符无效".
  // Mirrors WebLoginPanel's loadAuthRuntime path.
  useEffect(() => {
    if (!customerPhone) return
    let cancelled = false
    loadAuthRuntime(lng)
      .then((runtime) => {
        if (!cancelled) setAuthRuntime(runtime)
      })
      .catch((runtimeError) => {
        if (!cancelled) {
          setError(
            friendlyDeleteError(
              lng,
              runtimeError instanceof Error
                ? runtimeError.message
                : "initialize_auth_runtime_failed"
            )
          )
        }
      })
    return () => {
      cancelled = true
    }
  }, [customerPhone, lng])

  useEffect(() => {
    if (resendCountdown <= 0) return
    const timer = window.setTimeout(
      () => setResendCountdown((value) => Math.max(0, value - 1)),
      1000
    )
    return () => window.clearTimeout(timer)
  }, [resendCountdown])

  useEffect(() => {
    if (state === "otp_sent" && otpInputRef.current) {
      otpInputRef.current.focus()
    }
  }, [state])

  // Close on Escape.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && state !== "deleting" && state !== "done") {
        onClose()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose, state])

  // Render the Turnstile widget only on the otp_idle step (where the send
  // button lives). The upstream sendsms endpoint requires a fresh Turnstile
  // token, so we tear it down whenever we leave the idle step and let a new
  // render happen if the user comes back via resend.
  useEffect(() => {
    if (state !== "otp_idle") {
      // Tear down any leftover widget so the next mount renders fresh.
      const id = turnstileWidgetIdRef.current
      if (id && (window as any).turnstile?.remove) {
        try {
          ;(window as any).turnstile.remove(id)
        } catch {
          // ignore — widget already gone
        }
      }
      turnstileWidgetIdRef.current = null
      return
    }
    if (turnstileWidgetIdRef.current) return
    const container = turnstileContainerRef.current
    if (!container) return

    let cancelled = false
    const tryRender = (attempt = 0) => {
      if (cancelled) return
      const ts = (window as any).turnstile
      if (!ts?.render) {
        if (attempt < 40) {
          window.setTimeout(() => tryRender(attempt + 1), 250)
        }
        return
      }
      container.innerHTML = ""
      const widgetId = ts.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        size: "normal",
        action: "delete_account",
        callback: (token: string) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(""),
        "expired-callback": () => setTurnstileToken(""),
        "timeout-callback": () => setTurnstileToken(""),
      })
      if (widgetId) turnstileWidgetIdRef.current = widgetId
    }

    tryRender()
    return () => {
      cancelled = true
    }
  }, [state])

  // After a successful send, reset the Turnstile so the next "resend" press
  // has a fresh token. Without this the widget keeps the consumed token and
  // upstream rejects on the second send.
  const resetTurnstile = useCallback(() => {
    const id = turnstileWidgetIdRef.current
    setTurnstileToken("")
    if (id && (window as any).turnstile?.reset) {
      try {
        ;(window as any).turnstile.reset(id)
      } catch {
        // ignore — widget will refresh on next render
      }
    }
  }, [])

  const maskedPhone = customerPhone
    ? customerPhone.replace(/.(?=.{4})/g, "•")
    : ""

  const sendCode = useCallback(async (targetChannel: DeleteOtpChannel) => {
    if (!customerPhone) return
    setChannel(targetChannel)
    if (!authRuntime) {
      setError(accountText(lng, "account.delete.errorRuntimeNotReady"))
      return
    }
    const debugMode = isDebugModeHost()
    if (!turnstileToken && !debugMode) {
      setError(accountText(lng, "account.delete.errorNeedTurnstile"))
      return
    }
    setError("")
    setState("otp_sending")
    trackYohoEvent("account_delete_otp_send", {
      delivery_channel: targetChannel,
    })
    try {
      const response = await fetch("/api/auth/sms/send", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
          "visitor-id": authRuntime.visitorId,
        },
        body: JSON.stringify({
          phone: customerPhone,
          type: targetChannel,
          locale: normalizeLocaleForSms(lng),
          t: turnstileToken,
          device_fingerprint: authRuntime.visitorId,
          integrity_token: authRuntime.integrityToken,
          // The dev backend (eapi-dev.yohomobile.dev) has no TURNSTILE_SECRET_KEY
          // configured, so every siteverify call fails with TURNSTILE_TOKEN_INVALID.
          // Flutter does exactly the same dance — see the bundle's
          // `if($.nt){p.m(0,"debug_mode",!0)}` block. Backend skips Turnstile
          // when debug_mode=true AND NODE_ENV!==production.
          ...(debugMode ? { debug_mode: true } : {}),
        }),
      })
      const result = (await response.json().catch(() => null)) as {
        success?: boolean
        message?: string
        error_code?: string | null
      } | null
      if (!response.ok || result?.success === false) {
        throw new Error(
          result?.error_code || result?.message || "send_code_failed"
        )
      }
      setState("otp_sent")
      setResendCountdown(60)
      resetTurnstile()
    } catch (sendError) {
      const code =
        sendError instanceof Error ? sendError.message : "send_code_failed"
      setError(getDeleteSendErrorMessage(lng, targetChannel, code))
      setState("otp_idle")
      resetTurnstile()
    }
  }, [authRuntime, customerPhone, lng, resetTurnstile, turnstileToken])

  const selectChannel = useCallback(
    (nextChannel: DeleteOtpChannel) => {
      if (nextChannel === channel || state === "otp_sending") return
      setChannel(nextChannel)
      setError("")
      setCode("")
      trackYohoEvent("account_delete_otp_channel_select", {
        delivery_channel: nextChannel,
      })
      if (state === "otp_sent") {
        resetTurnstile()
        setState("otp_idle")
      }
    },
    [channel, resetTurnstile, state]
  )

  const confirmDelete = useCallback(async () => {
    if (code.length < 4) return
    setError("")
    setState("deleting")
    trackYohoEvent("account_delete_confirmed", { delivery_channel: channel })
    try {
      const response = await fetch("/api/account/delete", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
          ...(authRuntime
            ? { "visitor-id": authRuntime.visitorId }
            : {}),
        },
        body: JSON.stringify({
          code,
          delivery_channel: channel,
          locale: normalizeLocaleForSms(lng),
          ...(authRuntime
            ? {
                device_fingerprint: authRuntime.visitorId,
                integrity_token: authRuntime.integrityToken,
              }
            : {}),
        }),
      })
      const result = (await response.json().catch(() => null)) as {
        success?: boolean
        message?: string
      } | null
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || "delete_account_failed")
      }
      trackYohoEvent("account_delete_success")
      setState("done")
      // Give the toast a beat, then bounce them home (and logout client-side).
      window.setTimeout(() => {
        void auth.logout({
          redirectTo: localizeWebAccountPath("/account", lng),
        })
      }, 1500)
    } catch (deleteError) {
      const errCode =
        deleteError instanceof Error
          ? deleteError.message
          : "delete_account_failed"
      trackYohoEvent("account_delete_failed", { error_kind: errCode })
      setError(friendlyDeleteError(lng, errCode))
      setState("otp_sent")
    }
  }, [auth, authRuntime, channel, code, lng])

  const channelLabel = accountText(
    lng,
    channel === "whatsapp" ? "WhatsApp" : "SMS"
  )
  const switchChannelLabel = getDeleteSwitchChannelLabel(lng, channel)
  const sentMessage = accountText(
    lng,
    channel === "whatsapp" ? "WhatsApp code sent." : "SMS code sent."
  )
  const ChannelSelector = (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
          {accountText(lng, "Currently using")}
        </span>
        <span className="text-xs font-bold text-slate-700">{channelLabel}</span>
      </div>
      <div
        role="radiogroup"
        aria-label={accountText(lng, "Choose a method to continue.")}
        className="grid grid-cols-2 gap-1 rounded-2xl bg-slate-100 p-1"
      >
        {DELETE_OTP_CHANNELS.map((item) => {
          const selected = channel === item.value
          const Icon = item.icon
          return (
            <button
              key={item.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={state === "otp_sending" || state === "deleting"}
              onClick={() => selectChannel(item.value)}
              className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 text-sm font-extrabold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:cursor-not-allowed disabled:opacity-60 ${
                selected
                  ? "bg-white text-orange-600 shadow-sm ring-1 ring-orange-100"
                  : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {accountText(lng, item.labelKey)}
              {selected ? <Check className="h-4 w-4" aria-hidden /> : null}
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-stone-950/60 px-4 py-6 sm:items-center"
      onClick={(event) => {
        if (event.target === event.currentTarget && state !== "deleting") {
          onClose()
        }
      }}
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-[0_20px_60px_-20px_rgba(15,23,42,0.45)]">
        <div className="flex items-start justify-between gap-4 border-b border-rose-100 bg-rose-50/60 px-6 py-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-rose-600 text-white">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <h2 className="text-lg font-extrabold text-rose-700">
                {accountText(lng, "account.delete.title")}
              </h2>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-rose-500">
                {accountText(lng, "account.delete.subtitle")}
              </p>
            </div>
          </div>
          {state !== "deleting" && state !== "done" ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={accountText(lng, "Cancel")}
              className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-white hover:text-slate-700"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
                <path
                  d="M6 6l12 12M6 18L18 6"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
        </div>

        <div className="px-6 py-5">
          {state === "needs_phone" ? (
            <div className="grid gap-4 text-sm leading-6 text-slate-700">
              <p>{accountText(lng, "account.delete.needPhoneBody")}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onEditProfile}
                  className="inline-flex min-h-11 items-center justify-center rounded-full bg-orange-600 px-5 text-sm font-extrabold text-white transition hover:bg-orange-700"
                >
                  {accountText(lng, "account.delete.goBindPhone")}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50"
                >
                  {accountText(lng, "Cancel")}
                </button>
              </div>
            </div>
          ) : null}

          {state === "warn" ? (
            <div className="grid gap-4 text-sm leading-6 text-slate-700">
              <p className="font-semibold text-slate-900">
                {accountText(lng, "account.delete.warningBody")}
              </p>
              <ul className="grid gap-1.5 rounded-2xl bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
                <li>• {accountText(lng, "account.delete.lossEsim")}</li>
                <li>• {accountText(lng, "account.delete.lossBalance")}</li>
                <li>• {accountText(lng, "account.delete.lossOrders")}</li>
                <li>• {accountText(lng, "account.delete.lossReferrals")}</li>
                <li>• {accountText(lng, "account.delete.lossLogins")}</li>
              </ul>
              <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold leading-5 text-amber-800">
                {accountText(lng, "account.delete.reuseNotice")}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setState("otp_idle")}
                  className="inline-flex min-h-11 items-center justify-center rounded-full bg-rose-600 px-5 text-sm font-extrabold text-white transition hover:bg-rose-700"
                >
                  {accountText(lng, "account.delete.startCta")}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50"
                >
                  {accountText(lng, "Cancel")}
                </button>
              </div>
            </div>
          ) : null}

          {state === "otp_idle" || state === "otp_sending" ? (
            <div className="grid gap-4 text-sm leading-6 text-slate-700">
              <p>
                {accountText(lng, "account.delete.sendCodeTo", {
                  phone: maskedPhone,
                })}
              </p>
              <div ref={turnstileContainerRef} aria-hidden />
              <div className="grid gap-2 sm:grid-cols-2">
                {DELETE_OTP_CHANNELS.map((item) => {
                  const Icon = item.icon
                  const sendingThisChannel =
                    state === "otp_sending" && channel === item.value
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => void sendCode(item.value)}
                      disabled={
                        state === "otp_sending" ||
                        !authRuntime ||
                        (!turnstileToken && !isDebugModeHost())
                      }
                      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 text-sm font-extrabold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:cursor-not-allowed disabled:opacity-60 ${
                        item.value === "whatsapp"
                          ? "bg-orange-600 text-white hover:bg-orange-700"
                          : "border border-slate-200 bg-white text-slate-800 hover:border-orange-200 hover:bg-orange-50"
                      }`}
                    >
                      {sendingThisChannel ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Icon className="h-4 w-4" aria-hidden />
                      )}
                      {getDeleteSendCodeLabel(lng, item.value)}
                    </button>
                  )
                })}
              </div>
              {error ? (
                <p className="text-xs font-semibold text-rose-600">{error}</p>
              ) : null}
            </div>
          ) : null}

          {state === "otp_sent" || state === "deleting" ? (
            <div className="grid gap-4 text-sm leading-6 text-slate-700">
              {ChannelSelector}
              <p>
                <span className="font-semibold text-slate-900">
                  {sentMessage}
                </span>{" "}
                {accountText(lng, "account.delete.sendCodeTo", {
                  phone: maskedPhone,
                })}
              </p>
              <label className="grid gap-1.5">
                <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                  {accountText(lng, "account.delete.codeLabel")}
                </span>
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  value={code}
                  disabled={state === "deleting"}
                  onChange={(event) => {
                    const next = event.target.value
                      .replace(/\D/g, "")
                      .slice(0, 8)
                    setCode(next)
                    if (next.length === 6) {
                      // small UX touch: auto-fire confirm when 6 digits ready.
                      // Use a timeout so the value updates first in React.
                      window.setTimeout(() => {
                        if (state !== "deleting") void confirmDelete()
                      }, 0)
                    }
                  }}
                  className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-lg font-bold tracking-[0.3em] text-slate-950 outline-none ring-orange-200 focus:border-orange-500 focus:ring-2 disabled:opacity-60"
                />
              </label>
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                {resendCountdown > 0 ? (
                  <span>
                    {accountText(lng, "account.delete.resendIn", {
                      seconds: resendCountdown,
                    })}
                  </span>
                ) : (
                  <span className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        // Bounce back to the idle step so Turnstile re-renders
                        // with a fresh challenge — sendsms rejects re-used
                        // tokens on the upstream side.
                        setError("")
                        setCode("")
                        resetTurnstile()
                        setState("otp_idle")
                      }}
                      disabled={state === "deleting"}
                      className="font-bold text-orange-600 transition hover:text-orange-700 disabled:opacity-60"
                    >
                      {accountText(lng, "account.delete.resendCta")}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        selectChannel(
                          channel === "whatsapp" ? "sms" : "whatsapp"
                        )
                      }
                      disabled={state === "deleting"}
                      className="font-bold text-slate-600 transition hover:text-orange-700 disabled:opacity-60"
                    >
                      {switchChannelLabel}
                    </button>
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={code.length < 4 || state === "deleting"}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-rose-600 px-5 text-sm font-extrabold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {state === "deleting" ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Trash2 className="h-4 w-4" aria-hidden />
                )}
                {accountText(lng, "account.delete.confirmCta")}
              </button>
              {error ? (
                <p className="text-xs font-semibold text-rose-600">{error}</p>
              ) : null}
            </div>
          ) : null}

          {state === "done" ? (
            <div className="grid gap-3 text-sm leading-6 text-slate-700">
              <p className="font-bold text-emerald-600">
                {accountText(lng, "account.delete.success")}
              </p>
              <p className="text-xs text-slate-500">
                {accountText(lng, "account.delete.redirecting")}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

// Detect non-prod hosts where the upstream Turnstile secret isn't configured.
// On these hosts the backend would otherwise reject every send with
// TURNSTILE_TOKEN_INVALID. Flutter does the same thing via its own debug flag
// (`if($.nt){p.m(0,"debug_mode",!0)}` in the bundle).
function isDebugModeHost() {
  if (typeof window === "undefined") return false
  const host = window.location.hostname
  return (
    host === "yohomobile.dev" ||
    host === "www.yohomobile.dev" ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".yohomobile.dev")
  )
}

function normalizeLocaleForSms(lng: string) {
  // The SMS upstream wants xx_YY. Some of our locales are already in that shape
  // (zh_CN); others are bare like "en" or "ko". Match the convention used by
  // the existing /api/auth/sms/send route's `normalizeLocale` helper.
  return (lng || "en").replace("-", "_")
}

function ProfileInput({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
}) {
  return (
    <label className="grid gap-2 text-sm font-semibold text-slate-900">
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 rounded-xl border border-slate-200 bg-white px-4 text-base outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
      />
    </label>
  )
}

export function MyEsimWebPage({ lng }: LocaleProps) {
  const mounted = useRemoveSsrFallback("my-esim-ssr-fallback")
  const auth = useAuth()
  const searchParams = useSearchParams()
  const highlightOrder = searchParams.get("order") || ""
  const redirectTo = getCurrentRedirect(lng, "/esim")
  const shouldFetch = auth.status === "authenticated"
  const esims = useEapi(shouldFetch ? "store/get_esims" : null, [shouldFetch])
  // Parallel fetch the customer's orders so each EsimCard can render its
  // purchase receipt inline (date + amount + items). Cheap server-side once
  // /account/orders is gone — Orders is now the only consumer of receipt data.
  const orders = useEapi(
    shouldFetch ? "store/get_orders?expand=items,items.variant" : null,
    [shouldFetch]
  )

  useEffect(() => {
    if (!mounted || !shouldFetch || esims.loading || esims.error) return
    const esimItems = normalizeList(esims.data, [
      "esims",
      "esim_list",
      "items",
      "data",
    ])
    trackYohoEvent("my_esims_view", {
      active_esim_count: esimItems.length,
    })
  }, [esims.data, esims.error, esims.loading, mounted, shouldFetch])

  if (!mounted) {
    return null
  }

  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "My eSIM")}
      subtitle={accountText(
        lng,
        "View all your purchased eSIMs here. Check data usage, remaining days, and install new eSIMs with just a tap."
      )}
    >
      {auth.status === "loading" ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : !shouldFetch ? (
        <LoginRequired
          lng={lng}
          redirectTo={redirectTo}
          title={accountText(lng, "Sign in to manage your eSIMs")}
          description={accountText(
            lng,
            "Get installation QR codes, check activation status, and manage orders whenever you need."
          )}
          secondaryAction={{
            href: localizeWebAccountPath("/buy", lng),
            label: copyText(lng, "buyEsim"),
          }}
          features={[
            {
              icon: QrCode,
              title: accountText(lng, "Installation details"),
              description: accountText(
                lng,
                "View QR code and manual setup steps."
              ),
            },
            {
              icon: Smartphone,
              title: accountText(lng, "Usage status"),
              description: accountText(
                lng,
                "Check activation, remaining days, and next actions."
              ),
            },
            {
              icon: ReceiptText,
              title: accountText(lng, "Receipts"),
              description: accountText(
                lng,
                "Purchase receipts live inside each eSIM card."
              ),
            },
          ]}
        />
      ) : esims.loading ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : esims.error ? (
        <ErrorCard message={esims.error} onRetry={esims.reload} lng={lng} />
      ) : (
        <EsimListContent
          lng={lng}
          data={esims.data}
          onRefresh={() => {
            esims.reload()
            orders.reload()
          }}
          ordersData={orders.data}
          ordersLoading={orders.loading}
          highlightOrder={highlightOrder}
        />
      )}
    </PageShell>
  )
}

function EsimListContent({
  lng,
  data,
  onRefresh,
  ordersData,
  ordersLoading,
  highlightOrder,
}: LocaleProps & {
  data: unknown
  onRefresh: () => void
  ordersData: unknown
  ordersLoading: boolean
  highlightOrder: string
}) {
  const esims = normalizeList(data, ["esims", "esim_list", "items", "data"])
  const [statusFilter, setStatusFilter] = useState<EsimStatusFilter>("all")
  const filterOptions: EsimStatusFilter[] = [
    "all",
    "active",
    "notActive",
    "terminated",
  ]
  const filteredEsims =
    statusFilter === "all"
      ? esims
      : esims.filter((esim) => {
          const status = pickValue(esim, [
            "status",
            "esim_status",
            "state",
            "activation_status",
          ])
          return getEsimStatusFilter(status) === statusFilter
        })

  // Index orders by id for O(1) per-card lookup; each EsimCard renders its
  // matching purchase as an inline receipt.
  const ordersById = useMemo(() => {
    const list = normalizeList(ordersData, ["orders", "items", "data"])
    const map = new Map<string, AnyRecord>()
    for (const order of list) {
      const id = pickValue(order, ["id", "order_id"])
      if (id) map.set(id, order)
    }
    return map
  }, [ordersData])

  const hasHighlightMatch = highlightOrder
    ? esims.some(
        (esim) =>
          pickValue(esim, ["order_id", "orderId", "order"]) === highlightOrder
      )
    : true

  // Auto-retry once at ~6s if the customer just landed from /thankyou with an
  // ?order= but yoho-task fulfillment hasn't surfaced the eSIM yet. After the
  // retry we leave it to the manual Refresh button + the preparing banner.
  useEffect(() => {
    if (!highlightOrder || hasHighlightMatch) return
    const timer = window.setTimeout(() => onRefresh(), 6000)
    return () => window.clearTimeout(timer)
  }, [highlightOrder, hasHighlightMatch, onRefresh])

  return (
    <div className="grid gap-5">
      {highlightOrder && !hasHighlightMatch ? (
        <AccountCard className="border-amber-200 bg-amber-50 p-4 md:p-5">
          <div className="flex items-start gap-3">
            <Loader2
              className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-amber-700"
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-amber-900">
                {accountText(lng, "esim.preparingBanner")}
              </p>
              <p className="mt-1 text-xs leading-5 text-amber-800">
                {accountText(lng, "esim.preparingBannerHint")}
              </p>
              <button
                type="button"
                onClick={onRefresh}
                className="mt-3 inline-flex h-9 items-center gap-1.5 rounded-full bg-orange-600 px-4 text-xs font-bold text-white transition hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                {accountText(lng, "Refresh")}
              </button>
            </div>
          </div>
        </AccountCard>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <SecondaryLink href={localizeWebAccountPath("/buy", lng)}>
          {copyText(lng, "buyEsim")}
        </SecondaryLink>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900 transition hover:border-orange-200 hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {accountText(lng, "Refresh")}
        </button>
      </div>
      {esims.length > 0 && (
        <div
          className="flex flex-wrap gap-2"
          aria-label={accountText(lng, "Filter eSIM status")}
        >
          {filterOptions.map((option) => {
            const active = statusFilter === option
            return (
              <button
                key={option}
                type="button"
                onClick={() => setStatusFilter(option)}
                className={`inline-flex min-h-9 items-center justify-center rounded-full px-4 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${
                  active
                    ? "bg-slate-950 text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                }`}
                aria-pressed={active}
              >
                {esimStatusFilterLabel(lng, option)}
              </button>
            )
          })}
        </div>
      )}
      {esims.length === 0 ? (
        <AccountCard className="p-6 text-center">
          <IconBox icon={Smartphone} tone="blue" />
          <h2 className="mt-4 text-xl font-semibold">
            {accountText(lng, "No eSIM yet")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {accountText(
              lng,
              "Your purchased eSIM will appear here within 2 minutes"
            )}
          </p>
        </AccountCard>
      ) : filteredEsims.length === 0 ? (
        <AccountCard className="p-6 text-center">
          <IconBox icon={Smartphone} tone="blue" />
          <h2 className="mt-4 text-xl font-semibold">
            {accountText(lng, "No matching eSIMs")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {accountText(
              lng,
              "Switch filters to view eSIMs in other statuses."
            )}
          </p>
        </AccountCard>
      ) : (
        <DataGrid>
          {buildEsimDisplayUnits(filteredEsims).map((unit, index) => {
            const primaryEsim = unit.primary
            const esimOrderId = pickValue(primaryEsim, [
              "order_id",
              "orderId",
              "order",
            ])
            const order = esimOrderId
              ? ordersById.get(esimOrderId) || null
              : null
            const highlighted =
              !!highlightOrder &&
              unit.all.some(
                (e) =>
                  pickValue(e, ["order_id", "orderId", "order"]) ===
                  highlightOrder
              )
            const cardKey =
              pickValue(primaryEsim, ["id", "esim_id", "iccid"]) || `unit-${index}`
            if (unit.backups.length === 0) {
              return (
                <EsimCard
                  key={cardKey}
                  lng={lng}
                  esim={primaryEsim}
                  order={order}
                  ordersById={ordersById}
                  ordersLoading={ordersLoading}
                  highlighted={highlighted}
                />
              )
            }
            return (
              <GroupedFreeEsimCard
                key={cardKey}
                lng={lng}
                primary={primaryEsim}
                backups={unit.backups}
                ordersById={ordersById}
                ordersLoading={ordersLoading}
                highlightOrder={highlightOrder}
                highlighted={highlighted}
              />
            )
          })}
        </DataGrid>
      )}
    </div>
  )
}

type EsimDisplayUnit = {
  primary: AnyRecord
  backups: AnyRecord[]
  all: AnyRecord[]
}

/**
 * Group same-variant free eSIMs into a single display unit so customers don't
 * see five identical "Order #X" cards after repeatedly claiming the free
 * promo. Non-free eSIMs always stay as their own unit (every paid purchase
 * is unique). The primary inside a free group is whichever copy is active,
 * else the most recently created — so the user sees what they're actually
 * using, with the leftover copies tucked into a "More copies" expander.
 *
 * Preserves original ordering: a unit's position in the output is the
 * position of its first member in the input.
 */
function buildEsimDisplayUnits(esims: AnyRecord[]): EsimDisplayUnit[] {
  const consumed = new Set<number>()
  const units: EsimDisplayUnit[] = []
  esims.forEach((esim, index) => {
    if (consumed.has(index)) return
    if (!isFreeEsimRecord(esim)) {
      units.push({ primary: esim, backups: [], all: [esim] })
      return
    }
    const variantKey = pickValue(esim, ["variant_id", "variantId"])
    if (!variantKey) {
      units.push({ primary: esim, backups: [], all: [esim] })
      return
    }
    const groupIndices: number[] = [index]
    for (let j = index + 1; j < esims.length; j++) {
      if (consumed.has(j)) continue
      const other = esims[j]
      if (!isFreeEsimRecord(other)) continue
      if (pickValue(other, ["variant_id", "variantId"]) !== variantKey) continue
      groupIndices.push(j)
    }
    groupIndices.forEach((i) => consumed.add(i))
    const members = groupIndices.map((i) => esims[i])
    const primaryIdx = pickPrimaryEsimIndex(members)
    const primary = members[primaryIdx]
    const backups = members.filter((_, i) => i !== primaryIdx)
    units.push({ primary, backups, all: members })
  })
  return units
}

function pickPrimaryEsimIndex(members: AnyRecord[]): number {
  // 1) Prefer an active eSIM (the one the user is currently using).
  for (let i = 0; i < members.length; i++) {
    const status = pickValue(members[i], [
      "status",
      "esim_status",
      "state",
      "activation_status",
    ]).toLowerCase()
    if (status.includes("active") || status === "active") return i
  }
  // 2) Else newest by created_at (Strapi creates eSIMs in chronological order).
  let bestIdx = 0
  let bestDate = ""
  members.forEach((member, i) => {
    const date = pickValue(member, ["created_at", "createdAt", "create_datetime"])
    if (date > bestDate) {
      bestDate = date
      bestIdx = i
    }
  })
  return bestIdx
}

function GroupedFreeEsimCard({
  lng,
  primary,
  backups,
  ordersById,
  ordersLoading,
  highlightOrder,
  highlighted,
}: LocaleProps & {
  primary: AnyRecord
  backups: AnyRecord[]
  ordersById: Map<string, AnyRecord>
  ordersLoading: boolean
  highlightOrder: string
  highlighted: boolean
}) {
  const primaryOrderId = pickValue(primary, ["order_id", "orderId", "order"])
  const primaryOrder = primaryOrderId
    ? ordersById.get(primaryOrderId) || null
    : null

  return (
    <div className="grid gap-2">
      <EsimCard
        lng={lng}
        esim={primary}
        order={primaryOrder}
        ordersById={ordersById}
        ordersLoading={ordersLoading}
        highlighted={highlighted}
      />
      <details className="group overflow-hidden rounded-2xl border border-stone-200 bg-white transition-colors hover:bg-stone-50">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 [&::-webkit-details-marker]:hidden">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-emerald-600" aria-hidden />
            <span className="text-sm font-semibold text-stone-900">
              {accountText(lng, "freeGroup.backupCount", {
                count: backups.length,
              })}
            </span>
          </div>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-stone-500 transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <div className="border-t border-stone-200 px-4 py-3">
          <p className="text-xs leading-5 text-stone-500">
            {accountText(lng, "freeGroup.backupDesc")}
          </p>
          <ul className="mt-3 grid gap-2">
            {backups.map((backup, index) => {
              const backupId = getEsimId(backup)
              const backupIccid = pickValue(backup, ["iccid", "ICCID"])
              const backupStatus = pickValue(backup, [
                "status",
                "esim_status",
                "state",
                "activation_status",
              ])
              const backupCreated = formatDate(
                pickValue(backup, [
                  "created_at",
                  "createdAt",
                  "create_datetime",
                ])
              )
              const installHref = localizeWebAccountPath(
                `/account/esim/${encodeURIComponent(backupId)}/installation`,
                lng
              )
              const backupOrderId = pickValue(backup, [
                "order_id",
                "orderId",
                "order",
              ])
              const isHighlighted =
                !!highlightOrder && backupOrderId === highlightOrder
              return (
                <li
                  key={backupId || index}
                  className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 transition ${
                    isHighlighted
                      ? "border-orange-300 bg-orange-50"
                      : "border-stone-200 bg-white"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-[11px] text-stone-600">
                      {backupIccid || backupId}
                    </p>
                    <p className="mt-0.5 text-[11px] text-stone-500">
                      {backupCreated}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={backupStatus} lng={lng} />
                    <Link
                      href={installHref}
                      className="inline-flex h-8 items-center gap-1 rounded-full bg-orange-500 px-3 text-[11px] font-extrabold text-white transition hover:bg-orange-600"
                    >
                      {accountText(lng, "freeGroup.installBackup")}
                    </Link>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </details>
    </div>
  )
}

function getEsimId(esim: AnyRecord) {
  return (
    pickValue(esim, ["id", "esim_id", "iccid", "imsi", "order_item_id"]) ||
    "unknown"
  )
}

function isFreeEsimRecord(esim: AnyRecord) {
  // Authoritative signal: the variant the eSIM was bought against carries
  // `base_free_esim: true` in its metadata when it was a free-claim variant
  // (see checkout-data.ts line 1550 + variant_metadata schema). This is what
  // backend uses to gate the __HIT_FREE__ discount, so it's the truth.
  const variantMeta = (esim.variant_metadata || {}) as AnyRecord
  const baseFreeFlag = variantMeta.base_free_esim
  if (baseFreeFlag === true || baseFreeFlag === "true") return true

  // Legacy / fallback signals — explicit fields some older records carried.
  const explicitValue = pickValue(esim, [
    "is_free",
    "isFree",
    "is_free_esim",
    "free_esim",
    "free",
  ]).toLowerCase()
  if (["1", "true", "yes"].includes(explicitValue)) return true

  // Last-ditch string-match fallback for records that lost their metadata.
  const searchable = [
    "name",
    "title",
    "package_name",
    "product_title",
    "plan_name",
    "description",
    "order_type",
    "type",
    "source",
  ]
    .map((key) => pickValue(esim, [key]))
    .join(" ")
    .toLowerCase()

  return searchable.includes("free") || searchable.includes("免费")
}

export function getSafeEsimTrackingId(esim: AnyRecord) {
  return (
    pickValue(esim, ["id", "esim_id", "order_item_id", "imsi"]) || "unknown"
  )
}

function getEsimUsageState(esim: AnyRecord) {
  const status = pickValue(esim, [
    "status",
    "esim_status",
    "state",
    "activation_status",
  ])
  if (status) return status

  const daysRemaining = pickValue(esim, [
    "days_remaining",
    "daysRemaining",
    "remaining_days",
  ])
  if (daysRemaining) return "has_remaining_days"

  return "unknown"
}

function EsimCard({
  lng,
  esim,
  order,
  ordersById,
  ordersLoading,
  highlighted,
}: LocaleProps & {
  esim: AnyRecord
  order: AnyRecord | null
  ordersById: Map<string, AnyRecord>
  ordersLoading: boolean
  highlighted: boolean
}) {
  const id = getEsimId(esim)
  // Title uses the customer-facing 6-digit order display id (e.g. "#625634")
  // instead of a generic "Travel eSIM" label. order_display_id is shipped on
  // every get_esims record by upstream; we keep a last-8-of-order_id fallback
  // so support can still quote *something* if upstream regresses.
  const orderId = pickValue(esim, ["order_id", "orderId", "order"])
  const orderDisplayId =
    pickValue(esim, [
      "order_display_id",
      "display_id",
      "orderDisplayId",
    ]) || pickValue(order || {}, ["display_id"])
  const orderIdShort =
    orderDisplayId || (orderId ? orderId.slice(-8).toUpperCase() : "")
  const title = orderIdShort
    ? accountText(lng, "Order #{{id}}", { id: orderIdShort })
    : pickValue(esim, [
        "name",
        "title",
        "package_name",
        "product_title",
        "country",
        "plan_name",
      ]) || accountText(lng, "Travel eSIM")
  const cardRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [highlighted])
  const status = pickValue(esim, [
    "status",
    "esim_status",
    "state",
    "activation_status",
  ])
  const iccid = pickValue(esim, ["iccid", "ICCID"])
  const countryCode = pickValue(esim, [
    "country_code",
    "countryCode",
    "country_iso2",
    "iso2",
  ])
  const operator = pickValue(esim, [
    "operator",
    "carrier",
    "network",
    "operator_name",
  ])
  const flag = countryCode ? countryToFlag(countryCode) : ""

  // Data usage — only render if API returns both used + total in MB/GB
  const dataUsedRaw = pickValue(esim, [
    "data_used",
    "used_data",
    "usedMB",
    "used",
  ])
  const dataTotalRaw = pickValue(esim, [
    "data_total",
    "total_data",
    "totalMB",
    "total",
    "quota",
  ])
  const dataUsed = parseFloat(dataUsedRaw || "0")
  const dataTotal = parseFloat(dataTotalRaw || "0")
  const hasUsage =
    Number.isFinite(dataUsed) && Number.isFinite(dataTotal) && dataTotal > 0
  const usagePct = hasUsage
    ? Math.min(100, Math.round((dataUsed / dataTotal) * 100))
    : 0
  const daysRemaining = pickValue(esim, [
    "days_remaining",
    "daysRemaining",
    "remaining_days",
  ])
  const validityDays = pickValue(esim, ["validity", "validity_days", "days"])

  // Decide primary action by status
  const statusLower = (status || "").toLowerCase()
  const isPending =
    statusLower.includes("pending") ||
    statusLower.includes("待激活") ||
    statusLower.includes("未激活") ||
    statusLower.includes("not_install") ||
    statusLower.includes("inactive")
  const primaryActionLabel = isPending
    ? accountText(lng, "Install now")
    : accountText(lng, "View install steps")
  const primaryActionHref = localizeWebAccountPath(
    `/account/esim/${encodeURIComponent(id)}/installation`,
    lng
  )
  const detailsHref = localizeWebAccountPath(
    `/account/esim/${encodeURIComponent(id)}`,
    lng
  )

  return (
    <div
      ref={cardRef}
      className={
        highlighted
          ? "rounded-[22px] ring-2 ring-orange-400 ring-offset-2 ring-offset-white"
          : ""
      }
    >
      <AccountCard className="p-5 md:p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-base font-bold text-stone-900 md:text-lg">
              {flag && (
                <span className="text-xl" aria-hidden="true">
                  {flag}
                </span>
              )}
              <span className="truncate">{title}</span>
            </div>
            {operator && (
              <p className="mt-0.5 text-xs text-stone-500 md:text-sm">
                {operator}
              </p>
            )}
          </div>
          <StatusBadge status={status} lng={lng} />
        </div>

        {/* Compact one-line product summary so the customer knows what this
            eSIM actually is without opening detail (coverage + data + days
            + headline feature). Renders only when we have at least one
            datapoint — the variant_metadata is shipped for every modern
            order, but older records can be sparse. */}
        <EsimSpecSummary lng={lng} esim={esim} />

        {/* Data usage intentionally hidden on the list card — the detail page
            (with progress bar + percentage) is the dedicated surface for it.
            Validity hint still helps customers scan unused plans at a glance. */}
        {!hasUsage && validityDays && (
          <p className="mb-4 text-xs text-stone-500 md:text-sm">
            {accountText(lng, "Validity")}:{" "}
            <span className="font-semibold text-stone-700">
              {validityDays}
              {String(validityDays).match(/\d$/) ? accountText(lng, " days") : ""}
            </span>
          </p>
        )}

        {iccid && (
          <p className="mb-4 font-mono text-[11px] text-stone-400">
            ICCID · {iccid}
          </p>
        )}

        <EsimReceiptTimeline
          lng={lng}
          esim={esim}
          order={order}
          ordersById={ordersById}
          ordersLoading={ordersLoading}
          esimHasOrderId={!!orderId}
        />

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <PrimaryActionLink href={primaryActionHref}>
            {primaryActionLabel}
          </PrimaryActionLink>
          <MutedLink href={detailsHref}>{accountText(lng, "Details")}</MutedLink>
        </div>
      </AccountCard>
    </div>
  )
}

/**
 * Reusable plan-info card. Renders coverage, quota, validity, operators,
 * APN, install deadline, feature pills, the localized description ("more
 * information" panel) and notice — all from a single `variantMetadata`
 * object so the same component works on the eSIM detail page (where the
 * metadata is the Strapi esim snapshot) and on checkout (where the
 * metadata is the Medusa cart line_item.variant.metadata).
 *
 * Exported so checkout can drop it in without reinventing the description
 * + APN + features layout.
 */
export function PlanInfoCard({
  lng,
  variantMetadata,
}: LocaleProps & { variantMetadata: AnyRecord }) {
  // extractEsimSpecs reads from esim.variant_metadata first then falls back
  // to esim top-level fields. Wrapping a synthetic esim around our metadata
  // hits the variant_metadata-only path uniformly.
  const esimLike = { variant_metadata: variantMetadata }
  const specs = extractEsimSpecs(esimLike)
  const networkSummary = summarizeNetwork(specs.network)
  const description = getLocalizedDescription(esimLike, lng)
  const notice = getLocalizedNotice(esimLike, lng)
  const providerName = formatProviderName(specs.provider)
  const installBy = formatDate(specs.installEndTime)

  // Build the field list dynamically — skip rows that have no value so the
  // card stays clean for sparse legacy records.
  type FieldEntry = { label: string; value: ReactNode }
  const rows: FieldEntry[] = []
  if (specs.supportCountries.length > 0) {
    rows.push({
      label: accountText(lng, "plan.coverage"),
      value: (
        <span className="flex flex-wrap items-center gap-1.5">
          {specs.supportCountries.map((code) => {
            const flag = countryToFlag(code)
            const name = (() => {
              try {
                return (
                  new Intl.DisplayNames([lng], {
                    type: "region",
                    fallback: "code",
                  }).of(code) || code
                )
              } catch {
                return code
              }
            })()
            return (
              <span
                key={code}
                className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-700"
              >
                {flag ? (
                  <span className="text-sm" aria-hidden>
                    {flag}
                  </span>
                ) : null}
                {name}
              </span>
            )
          })}
        </span>
      ),
    })
  }
  if (specs.isUnlimited) {
    rows.push({
      label: accountText(lng, "plan.dataQuota"),
      value: (
        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
          {accountText(lng, "plan.unlimited")}
          {specs.quotaMB
            ? ` · ${accountText(lng, "plan.unlimitedHighSpeed", {
                quota: formatQuotaLabel(specs.quotaMB),
              })}`
            : ""}
        </span>
      ),
    })
  } else if (specs.quotaMB) {
    rows.push({
      label: accountText(lng, "plan.dataQuota"),
      value: (
        <span className="font-semibold text-stone-900">
          {formatQuotaLabel(specs.quotaMB)}
        </span>
      ),
    })
  }
  if (specs.validityDays !== null && specs.validityDays > 0) {
    const dailySuffix = specs.isDaily
      ? ` · ${accountText(lng, "plan.daily")}`
      : ""
    rows.push({
      label: accountText(lng, "plan.validity"),
      value: (
        <span className="font-semibold text-stone-900">
          {accountText(lng, "plan.daysLabel", { days: specs.validityDays })}
          {dailySuffix}
        </span>
      ),
    })
  }
  if (networkSummary.operators.length > 0) {
    rows.push({
      label: accountText(lng, "plan.operators"),
      value: networkSummary.operators.join(" · "),
    })
  }
  if (networkSummary.generations.length > 0) {
    rows.push({
      label: accountText(lng, "plan.networks"),
      value: networkSummary.generations.join(" / "),
    })
  }
  if (specs.apn) {
    rows.push({
      label: "APN",
      value: <span className="font-mono text-sm">{specs.apn}</span>,
    })
  }
  if (installBy) {
    rows.push({
      label: accountText(lng, "plan.installBy"),
      value: installBy,
    })
  }
  // Provider code (CMLink/Telna/...) intentionally NOT rendered — it's an
  // internal supplier name that doesn't help the customer.
  void providerName

  // Feature pills — same logic as the list card but allowed to grow here.
  const features: { label: string; on: boolean }[] = [
    { label: accountText(lng, "plan.hotspot"), on: specs.canHotspot },
    { label: accountText(lng, "plan.phone"), on: specs.canPhoneNumber },
    { label: accountText(lng, "plan.topupSupport"), on: specs.canTopup },
  ]
  const activeFeatures = features.filter((f) => f.on)

  if (rows.length === 0 && !description && activeFeatures.length === 0) {
    return null
  }

  return (
    <AccountCard className="p-5 md:p-6">
      <SectionTitle
        icon={Globe2}
        title={accountText(lng, "plan.title")}
      />
      {rows.length > 0 ? (
        <dl className="mt-5 grid gap-3 md:grid-cols-2">
          {rows.map((row) => (
            <FieldRow key={row.label} label={row.label} value={row.value} />
          ))}
        </dl>
      ) : null}
      {/* All sub-panels match the FieldRow shape (rounded-2xl + slate-50
          fill, no border) so every "small box" on this page reads as one
          consistent surface. */}
      {description ? (
        <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3">
          <p className="text-xs font-semibold uppercase text-slate-500">
            {accountText(lng, "plan.moreInfo")}
          </p>
          <p className="mt-1 break-words text-sm leading-6 text-slate-950">
            {description}
          </p>
        </div>
      ) : null}
      {activeFeatures.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {activeFeatures.map((feature) => (
            <span
              key={feature.label}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
            >
              <Check className="h-3 w-3 text-emerald-600" aria-hidden />
              {feature.label}
            </span>
          ))}
        </div>
      ) : null}
      {notice ? (
        <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3">
          <p className="text-xs font-semibold uppercase text-amber-700">
            {accountText(lng, "plan.notice")}
          </p>
          <p className="mt-1 break-words text-sm leading-6 text-slate-950">
            {notice}
          </p>
        </div>
      ) : null}
    </AccountCard>
  )
}

function EsimSpecSummary({
  lng,
  esim,
}: LocaleProps & { esim: AnyRecord }) {
  const specs = extractEsimSpecs(esim)
  const parts: string[] = []
  if (specs.supportCountries.length > 0) {
    parts.push(formatCoverageLabel(specs.supportCountries, lng, 2))
  }
  if (specs.isUnlimited) {
    parts.push(accountText(lng, "plan.unlimited"))
  } else {
    const quotaLabel = formatQuotaLabel(specs.quotaMB)
    if (quotaLabel) parts.push(quotaLabel)
  }
  if (specs.validityDays !== null && specs.validityDays > 0) {
    const dailySuffix = specs.isDaily
      ? ` · ${accountText(lng, "plan.daily")}`
      : ""
    parts.push(
      accountText(lng, "plan.daysLabel", { days: specs.validityDays }) +
        dailySuffix
    )
  }

  // Feature pills — only show "on" features so we don't waste space telling
  // the user what their eSIM *can't* do.
  const features: string[] = []
  if (specs.canHotspot) features.push(accountText(lng, "plan.hotspot"))
  if (specs.canPhoneNumber) features.push(accountText(lng, "plan.phone"))

  if (parts.length === 0 && features.length === 0) return null

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1.5">
      {parts.length > 0 ? (
        <p className="text-xs text-stone-600 md:text-sm">{parts.join(" · ")}</p>
      ) : null}
      {features.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {features.map((label) => (
            <span
              key={label}
              className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700"
            >
              {label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function EsimReceiptTimeline({
  lng,
  esim,
  order,
  ordersById,
  ordersLoading,
  esimHasOrderId,
}: LocaleProps & {
  esim: AnyRecord
  order: AnyRecord | null
  ordersById: Map<string, AnyRecord>
  ordersLoading: boolean
  esimHasOrderId: boolean
}) {
  // Receipt block opens to a single timeline merging the original Medusa
  // order with every topup recorded against this eSIM in Strapi's
  // `topup-esims` collection (Flutter uses the same endpoint, see
  // public/main.dart.js: store/get_topup_history). Lazy: no fetch until the
  // customer expands the receipt — most eSIMs have zero topups.
  const [expanded, setExpanded] = useState(false)
  const esimId = getEsimId(esim)
  const topups = useEapi(
    expanded && esimId && esimId !== "unknown"
      ? `store/get_topup_history?esim_id=${encodeURIComponent(esimId)}`
      : null,
    [expanded, esimId]
  )

  // Show a slim skeleton while the parent get_orders is still in-flight and
  // we know this eSIM should have a match. Pure-empty case (no order_id on
  // the eSIM) just hides the section entirely.
  if (!order && ordersLoading && esimHasOrderId) {
    return (
      <div className="mb-1 rounded-2xl bg-slate-50 px-4 py-3">
        <div className="h-3 w-20 animate-pulse rounded-full bg-slate-200" />
        <div className="mt-2 h-5 w-32 animate-pulse rounded-full bg-slate-200" />
      </div>
    )
  }
  if (!order && !esimHasOrderId) return null

  const orderCurrency = pickValue(order || {}, ["currency_code", "currency"])
  const orderRawTotal = pickValue(order || {}, [
    "total",
    "total_amount",
    "amount_total",
    "grand_total",
    "subtotal",
  ])
  const orderDate = formatDate(
    pickValue(order || {}, ["created_at", "createdAt"])
  )
  const orderStatus = pickValue(order || {}, [
    "status",
    "fulfillment_status",
    "payment_status",
  ])
  const orderIsFree = isFreeAmount(orderRawTotal)
  const orderTotalDisplay = orderIsFree
    ? accountText(lng, "receipt.free")
    : formatMinorAmount(orderRawTotal, orderCurrency, lng)

  type TimelineEntry = {
    id: string
    date: string
    label: string
    amount: string
    isFree: boolean
    status: string
  }

  const topupList = expanded
    ? normalizeList(topups.data, ["data", "topup_history", "items"])
    : []
  const topupCount = topupList.length

  // Build timeline entries: original purchase first (if order exists), then
  // each topup. Sort descending by date so latest activity is at the top.
  const entries: TimelineEntry[] = []
  if (order) {
    entries.push({
      id: pickValue(order, ["id", "order_id"]) || "purchase",
      date: orderDate,
      label: accountText(lng, "receipt.purchase"),
      amount: orderTotalDisplay || accountText(lng, "receipt.amountUnavailable"),
      isFree: orderIsFree,
      status: orderStatus,
    })
  }
  topupList.forEach((t, index) => {
    // topup-esims in Strapi only carries provider/quota metadata — no money.
    // We get the real charged amount by joining back to the Medusa order via
    // order_id. When that order is outside the most-recent get_orders page,
    // we fall back to "—" rather than fake a number.
    const tOrderId = pickValue(t, ["order_id", "orderId"])
    const linkedOrder = tOrderId ? ordersById.get(tOrderId) : undefined
    const tCurrency = linkedOrder
      ? pickValue(linkedOrder, ["currency_code", "currency"]) || orderCurrency
      : orderCurrency
    const tRaw = linkedOrder
      ? pickValue(linkedOrder, [
          "total",
          "total_amount",
          "amount_total",
          "grand_total",
          "subtotal",
        ])
      : ""
    // Detect free topup either explicitly from the topup record's flags or
    // from the linked Medusa order having a zero total.
    const adsRewardActive = (t as AnyRecord).is_ads_reward_available
    const tFree =
      isFreeAmount(tRaw) ||
      adsRewardActive === true ||
      adsRewardActive === "true" ||
      pickValue(t, ["type", "topup_type"]).toLowerCase().includes("free") ||
      (t as AnyRecord).is_free_topup === true
    const tDate = formatDate(pickValue(t, ["created_at", "createdAt"]))
    const tStatus =
      pickValue(linkedOrder || {}, [
        "status",
        "fulfillment_status",
        "payment_status",
      ]) || pickValue(t, ["status"])
    entries.push({
      id: pickValue(t, ["id", "order_id"]) || `topup-${index}`,
      date: tDate,
      label: tFree
        ? accountText(lng, "receipt.freeTopup")
        : accountText(lng, "receipt.topup"),
      amount: tFree
        ? accountText(lng, "receipt.free")
        : formatMinorAmount(tRaw, tCurrency, lng) || "—",
      isFree: tFree,
      status: tStatus,
    })
  })
  entries.sort((a, b) => {
    if (!a.date && !b.date) return 0
    if (!a.date) return 1
    if (!b.date) return -1
    return a.date < b.date ? 1 : -1
  })

  if (entries.length === 0) return null

  return (
    <details
      className="group mb-1 overflow-hidden rounded-2xl bg-slate-50 transition-colors hover:bg-slate-100"
      onToggle={(e) => setExpanded((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-start justify-between gap-3 p-4 [&::-webkit-details-marker]:hidden">
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">
            {accountText(lng, "receipt.title")}
          </span>
          <p className="mt-1 text-[15px] font-bold text-stone-900">
            {orderTotalDisplay || accountText(lng, "receipt.amountUnavailable")}
          </p>
          <p className="mt-0.5 text-xs text-stone-500">
            {orderDate ? accountText(lng, "receipt.paidOn", { date: orderDate }) : ""}
            {expanded && topupCount > 0
              ? ` · ${accountText(lng, "receipt.topupsCount", { count: topupCount })}`
              : ""}
          </p>
        </div>
        <ChevronDown
          className="mt-1 h-4 w-4 shrink-0 text-stone-500 transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="border-t border-stone-200 px-4 py-3">
        {topups.loading && topupCount === 0 ? (
          <p className="text-xs text-stone-500">
            {accountText(lng, "receipt.loadingHistory")}
          </p>
        ) : (
          <ol className="grid gap-2.5">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-baseline justify-between gap-3 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-900">{entry.label}</p>
                  {entry.date ? (
                    <p className="text-[11px] text-stone-500">{entry.date}</p>
                  ) : null}
                </div>
                <span
                  className={`shrink-0 font-mono ${entry.isFree ? "text-emerald-700" : "text-stone-700"}`}
                >
                  {entry.amount}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </details>
  )
}

export function EsimDetailWebPage({ lng, esimId }: DetailProps) {
  return (
    <EsimDataPage
      lng={lng}
      esimId={esimId}
      mode="detail"
      title={accountText(lng, "eSIM Details")}
      subtitle={accountText(
        lng,
        "View eSIM details, installation, and related actions."
      )}
    />
  )
}

export function EsimInstallationWebPage({ lng, esimId }: DetailProps) {
  return (
    <EsimDataPage
      lng={lng}
      esimId={esimId}
      mode="installation"
      title={accountText(lng, "eSIM Installation")}
      subtitle={accountText(
        lng,
        "Use QR or manual activation details to install the eSIM."
      )}
    />
  )
}

function EsimDataPage({
  lng,
  esimId,
  title,
  subtitle,
  mode,
}: DetailProps & {
  title: string
  subtitle: string
  mode: "detail" | "installation"
}) {
  const auth = useAuth()
  const redirectTo = localizeWebAccountPath(
    `/esim/${encodeURIComponent(esimId)}${mode === "installation" ? "/installation" : ""}`,
    lng
  )
  const shouldFetch = auth.status === "authenticated"
  const esims = useEapi(shouldFetch ? "store/get_esims" : null, [
    shouldFetch,
    esimId,
  ])
  // Detail page needs the customer's orders so the receipt timeline can show
  // amount + topup history. Installation mode has nothing to do with money,
  // skip the fetch there.
  const orders = useEapi(
    shouldFetch && mode === "detail"
      ? "store/get_orders?expand=items,items.variant"
      : null,
    [shouldFetch, mode]
  )

  // Sub-pages of an eSIM all return to the eSIM list rather than the account
  // hub — that's the parent route the user expects to land on when leaving
  // detail / installation. "Back to Account" was confusing because /account
  // is the hub two levels up, not the immediate parent.
  return (
    <PageShell
      lng={lng}
      title={title}
      subtitle={subtitle}
      backHref={localizeWebAccountPath("/account/esim", lng)}
      backLabel={accountText(lng, "My eSIM")}
    >
      {auth.status === "loading" ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : !shouldFetch ? (
        <LoginRequired lng={lng} redirectTo={redirectTo} />
      ) : esims.loading ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : esims.error ? (
        <ErrorCard message={esims.error} onRetry={esims.reload} lng={lng} />
      ) : (
        <EsimDetailContent
          lng={lng}
          esimId={esimId}
          data={esims.data}
          mode={mode}
          ordersData={orders.data}
          ordersLoading={orders.loading}
        />
      )}
    </PageShell>
  )
}

function findEsim(data: unknown, esimId: string) {
  const esims = normalizeList(data, ["esims", "esim_list", "items", "data"])
  const decoded = decodeURIComponent(esimId)
  return (
    esims.find((esim) =>
      ["id", "esim_id", "iccid", "imsi", "order_item_id"].some(
        (key) => String(esim[key] || "") === decoded
      )
    ) ||
    esims[0] ||
    null
  )
}

function EsimDetailContent({
  lng,
  data,
  esimId,
  mode,
  ordersData,
  ordersLoading,
}: LocaleProps & {
  data: unknown
  esimId: string
  mode: "detail" | "installation"
  ordersData: unknown
  ordersLoading: boolean
}) {
  const esim = findEsim(data, esimId)

  useEffect(() => {
    if (!esim || mode !== "detail") return
    trackYohoEvent("esim_usage_view", {
      esim_id_hash: getSafeEsimTrackingId(esim),
      usage_state: getEsimUsageState(esim),
    })
  }, [esim, mode])

  // Same orders-by-id index that MyEsimWebPage builds — lets the receipt
  // timeline resolve topup amounts via order_id join (topup-esims itself
  // has no money column, see public/main.dart.js + yoho-v2 schema).
  const ordersById = useMemo(() => {
    const list = normalizeList(ordersData, ["orders", "items", "data"])
    const map = new Map<string, AnyRecord>()
    for (const order of list) {
      const id = pickValue(order, ["id", "order_id"])
      if (id) map.set(id, order)
    }
    return map
  }, [ordersData])

  if (!esim) {
    return (
      <ErrorCard
        message={accountText(lng, "This eSIM was not found.")}
        lng={lng}
      />
    )
  }

  if (mode === "installation") {
    return <InstallationPanel lng={lng} esim={esim} />
  }

  // Title uses the customer-facing order display id ("订单 #625889") — same
  // logic as EsimCard so the user sees the identifier they used to navigate
  // here. Last-8-of-order_id fallback for upstream regressions; generic plan
  // name as final fallback when order info is unavailable.
  const orderId = pickValue(esim, ["order_id", "orderId", "order"])
  const order = orderId ? ordersById.get(orderId) || null : null
  const orderDisplayId =
    pickValue(esim, [
      "order_display_id",
      "display_id",
      "orderDisplayId",
    ]) || pickValue(order || {}, ["display_id"])
  const orderIdShort =
    orderDisplayId || (orderId ? orderId.slice(-8).toUpperCase() : "")
  const headerTitle = orderIdShort
    ? accountText(lng, "Order #{{id}}", { id: orderIdShort })
    : pickValue(esim, [
        "name",
        "title",
        "package_name",
        "product_title",
        "country",
        "plan_name",
      ]) || accountText(lng, "Travel eSIM")

  const status = pickValue(esim, [
    "status",
    "esim_status",
    "state",
    "activation_status",
  ])
  const countryCode = pickValue(esim, [
    "country_code",
    "countryCode",
    "country_iso2",
    "iso2",
  ])
  const flag = countryCode ? countryToFlag(countryCode) : ""

  const dataUsed = parseFloat(
    pickValue(esim, ["data_used", "used_data", "usedMB", "used"]) || "0"
  )
  const dataTotal = parseFloat(
    pickValue(esim, [
      "data_total",
      "total_data",
      "totalMB",
      "total",
      "quota",
    ]) || "0"
  )
  const hasUsage =
    Number.isFinite(dataUsed) && Number.isFinite(dataTotal) && dataTotal > 0
  const usagePct = hasUsage
    ? Math.min(100, Math.round((dataUsed / dataTotal) * 100))
    : 0
  const daysRemaining = pickValue(esim, [
    "days_remaining",
    "daysRemaining",
    "remaining_days",
  ])

  const installHref = localizeWebAccountPath(
    `/account/esim/${encodeURIComponent(esimId)}/installation`,
    lng
  )

  return (
    <div className="grid gap-5">
      {/* Hero — compact one-row header with flag + order id + status */}
      <AccountCard className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[20px] font-bold text-stone-900 md:text-[22px]">
              {flag ? (
                <span className="text-2xl" aria-hidden="true">
                  {flag}
                </span>
              ) : null}
              <span className="truncate">{headerTitle}</span>
            </div>
          </div>
          <StatusBadge status={status} lng={lng} />
        </div>

        {hasUsage ? (
          <div className="mt-5">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
              <div
                className={`h-full rounded-full transition-all ${
                  usagePct >= 90
                    ? "bg-rose-500"
                    : usagePct >= 70
                      ? "bg-orange-500"
                      : "bg-emerald-500"
                }`}
                style={{ width: `${usagePct}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-stone-500 md:text-sm">
              {accountText(lng, "usage.percentUsed", { percent: usagePct })}
              {daysRemaining
                ? ` · ${daysRemaining}${accountText(lng, " days left")}`
                : ""}
            </p>
          </div>
        ) : null}
      </AccountCard>

      {/* Plan info — what the customer actually bought. Pulls from the eSIM's
          variant_metadata: coverage, operators, network generations, quota,
          validity, features, APN, install deadline. Without this card the
          user has no way to know what their eSIM does. */}
      <PlanInfoCard lng={lng} variantMetadata={esim.variant_metadata || {}} />

      {/* Technical info — ICCID/IMSI used only by support + manual setup. */}
      <AccountCard className="p-5 md:p-6">
        <SectionTitle
          icon={Smartphone}
          title={accountText(lng, "eSIM Details")}
        />
        <dl className="mt-5 grid gap-3 md:grid-cols-2">
          <FieldRow label="ICCID" value={pickValue(esim, ["iccid", "ICCID"])} />
          <FieldRow label="IMSI" value={pickValue(esim, ["imsi", "IMSI"])} />
          <FieldRow
            label={accountText(lng, "Created")}
            value={formatDate(pickValue(esim, ["created_at", "createdAt"]))}
          />
        </dl>
      </AccountCard>

      {/* Receipt timeline (original purchase + topups) — same component as
          EsimCard so the design stays consistent. Lazy-loads topup history
          when expanded. */}
      <AccountCard className="p-5 md:p-6">
        <SectionTitle
          icon={ReceiptText}
          title={accountText(lng, "receipt.title")}
        />
        <div className="mt-5">
          <EsimReceiptTimeline
            lng={lng}
            esim={esim}
            order={order}
            ordersById={ordersById}
            ordersLoading={ordersLoading}
            esimHasOrderId={!!orderId}
          />
        </div>
      </AccountCard>

      {/* Single action card — Installation. Additional info was removed
          because the backend endpoint is for noname EID/IMEI补充, not
          generic notes/nicknames, and was wired up with wrong fields. */}
      <AccountCard className="p-4 md:p-5">
        <SettingLink
          icon={QrCode}
          title={accountText(lng, "Installation")}
          description={accountText(
            lng,
            "View QR and manual installation details."
          )}
          href={installHref}
          tone="orange"
        />
      </AccountCard>
    </div>
  )
}


function InstallStep({
  step,
  icon: Icon,
  title,
  description,
  children,
}: {
  step: number
  icon: LucideIcon
  title: string
  description: string
  children?: ReactNode
}) {
  return (
    <AccountCard className="p-5 md:p-6">
      <div className="flex items-start gap-4">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-orange-200 bg-orange-50 text-sm font-bold text-orange-700"
          aria-hidden
        >
          {step}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-bold text-slate-950 md:text-lg">
              {title}
            </h3>
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-orange-600"
              aria-hidden
            >
              <Icon className="h-4 w-4" />
            </span>
          </div>
          <p className="mt-1.5 text-sm leading-6 text-slate-600">{description}</p>
          {children}
        </div>
      </div>
    </AccountCard>
  )
}

type RedemptionSource = "us" | "jp"

export function RedemptionWebPage({ lng }: LocaleProps) {
  const auth = useAuth()
  const redirectTo = localizeWebAccountPath("/account/redemption", lng)
  const [source, setSource] = useState<RedemptionSource>("us")

  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "Redeem eSIM")}
      subtitle={accountText(
        lng,
        "Your redeemed eSIM will appear in My eSIM within 2 minutes."
      )}
    >
      {auth.status === "loading" ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : auth.status !== "authenticated" ? (
        <LoginRequired
          lng={lng}
          redirectTo={redirectTo}
          title={accountText(lng, "Sign in to redeem an eSIM")}
          description={accountText(
            lng,
            "Enter your code to activate an eSIM you purchased or received."
          )}
          features={[
            {
              icon: Ticket,
              title: accountText(lng, "Redemption code"),
              description: accountText(
                lng,
                "Enter your 14-character redemption code."
              ),
            },
            {
              icon: Globe2,
              title: accountText(lng, "Multiple sources"),
              description: accountText(
                lng,
                "Both Amazon US and Amazon JP supported."
              ),
            },
            {
              icon: Smartphone,
              title: accountText(lng, "Ready in 2 minutes"),
              description: accountText(
                lng,
                "Redeemed eSIM shows up in My eSIM."
              ),
            },
          ]}
        />
      ) : (
        <div className="grid gap-5">
          <RedemptionSourcePicker
            lng={lng}
            source={source}
            onChange={setSource}
          />
          {source === "us" ? (
            <RedemptionForm lng={lng} />
          ) : (
            <RedemptionJpRedirect lng={lng} />
          )}
        </div>
      )}
    </PageShell>
  )
}

function RedemptionSourcePicker({
  lng,
  source,
  onChange,
}: LocaleProps & {
  source: RedemptionSource
  onChange: (next: RedemptionSource) => void
}) {
  const options: {
    id: RedemptionSource
    flag: string
    title: string
    subtitle: string
  }[] = [
    {
      id: "us",
      flag: "🇺🇸",
      title: "yohomobile.com",
      subtitle: "Amazon US",
    },
    {
      id: "jp",
      flag: "🇯🇵",
      title: "jpa.yohomobile.com",
      subtitle: "Amazon JP",
    },
  ]
  return (
    <AccountCard className="p-5 md:p-6">
      <SectionTitle
        icon={Globe2}
        title={accountText(lng, "Where did you buy the eSIM?")}
        description={accountText(
          lng,
          "Pick the source first — we'll show the right redemption flow."
        )}
      />
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {options.map((opt) => {
          const active = source === opt.id
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              className={`flex items-center gap-4 rounded-2xl border px-4 py-3 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${
                active
                  ? "border-orange-400 bg-orange-50/60"
                  : "border-stone-200 hover:border-orange-200 hover:bg-orange-50/30"
              }`}
              aria-pressed={active}
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-stone-50 text-2xl">
                {opt.flag}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-semibold text-stone-900">
                  {opt.title}
                </span>
                <span className="mt-0.5 block text-sm text-stone-500">
                  {opt.subtitle}
                </span>
              </span>
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${
                  active
                    ? "border-orange-500 bg-orange-500"
                    : "border-stone-300 bg-white"
                }`}
                aria-hidden="true"
              >
                {active ? <Check className="h-3 w-3 text-white" /> : null}
              </span>
            </button>
          )
        })}
      </div>
    </AccountCard>
  )
}

function RedemptionJpRedirect({ lng }: LocaleProps) {
  return (
    <AccountCard className="p-5 md:p-6">
      <SectionTitle
        icon={ArrowRight}
        title={accountText(lng, "Please redeem on jpa.yohomobile.com")}
        description={accountText(
          lng,
          "eSIMs purchased on Amazon JP are handled by jpa.yohomobile.com — please complete the redemption there."
        )}
      />
      <div className="mt-5">
        <a
          href="https://jpa.yohomobile.com/"
          target="_blank"
          rel="noopener noreferrer"
          data-disable-nprogress="true"
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-orange-600 px-5 text-sm font-bold text-white transition hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
        >
          {accountText(lng, "Go to jpa.yohomobile.com")}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </AccountCard>
  )
}

function RedemptionForm({ lng }: LocaleProps) {
  const auth = useAuth()
  const [code, setCode] = useState("")
  const [email, setEmail] = useState(auth.customer?.email || "")
  const [status, setStatus] = useState("")
  const [pending, setPending] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setPending(true)
    setStatus("")
    try {
      await fetchEapi("store/redem_esim", {
        method: "POST",
        body: JSON.stringify({ code: code.trim(), email: email.trim() }),
      })
      setStatus(
        accountText(
          lng,
          "Redeemed! Your eSIM will appear in My eSIM within 2 minutes."
        )
      )
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "redeem_failed")
    } finally {
      setPending(false)
    }
  }

  return (
    <AccountCard className="p-5 md:p-6">
      <SectionTitle
        icon={Ticket}
        title={accountText(lng, "Redemption details")}
        description={accountText(
          lng,
          "Please enter a valid 14-character redemption code"
        )}
      />
      <form className="mt-5 grid gap-4" onSubmit={submit}>
        <ProfileInput
          label={accountText(lng, "Redemption code")}
          value={code}
          onChange={setCode}
        />
        <ProfileInput
          label={accountText(lng, "Email")}
          value={email}
          onChange={setEmail}
          type="email"
        />
        <div className="flex flex-wrap items-center gap-3">
          <PrimaryButton type="submit" disabled={pending}>
            {pending
              ? copyText(lng, "loading")
              : accountText(lng, "Redeem eSIM")}
          </PrimaryButton>
          <SecondaryLink href={localizeWebAccountPath("/account/esim", lng)}>
            {accountText(lng, "View My eSIM")}
          </SecondaryLink>
          <p
            aria-live="polite"
            className="min-h-5 text-sm font-semibold text-slate-600"
          >
            {status}
          </p>
        </div>
      </form>
    </AccountCard>
  )
}

export function ReferralsWebPage({ lng }: LocaleProps) {
  const auth = useAuth()
  const redirectTo = localizeWebAccountPath("/account/referrals", lng)
  const customerId = auth.customer?.id || ""
  const shouldFetch = auth.status === "authenticated" && customerId
  const referer = useEapi(
    shouldFetch
      ? `store/get_referer_info?customer_id=${encodeURIComponent(customerId)}`
      : null,
    [shouldFetch, customerId]
  )
  const stats = useEapi(
    shouldFetch
      ? `store/get_referral_stats?customer_id=${encodeURIComponent(customerId)}`
      : null,
    [shouldFetch, customerId]
  )

  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "My Referrals")}
      subtitle={accountText(
        lng,
        "Earn $2 when a friend makes a qualifying purchase, and they get 10% off their first order."
      )}
    >
      {auth.status === "loading" ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : !shouldFetch ? (
        <LoginRequired
          lng={lng}
          redirectTo={redirectTo}
          title={accountText(lng, "Sign in to see your referral rewards")}
          description={accountText(
            lng,
            "Earn $2 per qualifying referral; your friend gets 10% off their first order."
          )}
          features={[
            {
              icon: UsersRound,
              title: accountText(lng, "Your referral link"),
              description: accountText(lng, "Copy and share with friends."),
            },
            {
              icon: Gift,
              title: accountText(lng, "$2 reward"),
              description: accountText(
                lng,
                "Earn $2 for every qualifying friend purchase."
              ),
            },
            {
              icon: ReceiptText,
              title: accountText(lng, "Live stats"),
              description: accountText(
                lng,
                "Track invitations and rewards in real time."
              ),
            },
          ]}
        />
      ) : referer.loading ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : (
        // store/get_referer_info returns 404 for customers who don't have a
        // referer record yet — which is the expected state for any user who
        // hasn't been referred by someone else. We don't use that response
        // for the share link anyway (Flutter builds it client-side), but we
        // still fetch it so future "you were referred by X" UI can pick it up.
        // Stats endpoint returns zeros for new accounts so the metrics render.
        <ReferralContent
          lng={lng}
          customerId={customerId}
          stats={stats.data}
        />
      )}
    </PageShell>
  )
}

function ReferralContent({
  lng,
  customerId,
  stats,
}: LocaleProps & {
  customerId: string
  stats: unknown
}) {
  const statInfo = firstObject(stats, ["stats", "data"]) || {}
  const [copied, setCopied] = useState(false)
  const [origin, setOrigin] = useState("")

  // Flutter constructs the share link client-side as `<origin>/?__referer_id=<id>`
  // — there's no upstream `link` / `referral_link` field. The customer.id IS
  // the referer identifier; the landing page handler reads __referer_id from
  // the URL query (see bgi.$0 in main.dart.js).
  useEffect(() => {
    if (typeof window !== "undefined") {
      setOrigin(window.location.origin)
    }
  }, [])

  const link =
    customerId && origin
      ? `${origin}/${lng}/?__referer_id=${encodeURIComponent(customerId)}`
      : ""

  const copyLink = async () => {
    if (!link) return
    await navigator.clipboard?.writeText(link)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1600)
  }

  return (
    <div className="grid gap-5">
      <AccountCard className="p-5 md:p-6">
        <SectionTitle
          icon={UsersRound}
          title={accountText(lng, "Your Referral Link")}
          description={accountText(
            lng,
            "When a friend signs up with your referral link and makes a qualifying purchase, both of you get rewarded."
          )}
        />
        <div className="mt-5 rounded-2xl bg-slate-50 p-4">
          <p className="break-all text-sm font-semibold text-slate-800">
            {link || copyText(lng, "empty")}
          </p>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={copyLink}
            disabled={!link}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-orange-600 px-5 text-sm font-bold text-white transition hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <Copy className="h-4 w-4" aria-hidden="true" />
            {copied ? copyText(lng, "copied") : copyText(lng, "copy")}
          </button>
        </div>
      </AccountCard>
      <DataGrid>
        <MetricCard
          icon={Gift}
          title={accountText(lng, "Total Rewards Earned")}
          value={String(pickValue(statInfo, ["total_rewards"]) ?? "0")}
        />
        <MetricCard
          icon={UsersRound}
          title={accountText(lng, "Friends Invited")}
          value={String(pickValue(statInfo, ["total_referrals"]) ?? "0")}
        />
        <MetricCard
          icon={ReceiptText}
          title={accountText(lng, "Pending rewards")}
          value={String(pickValue(statInfo, ["pending_rewards"]) ?? "0")}
        />
      </DataGrid>
      <AccountCard className="p-5 md:p-6">
        <SectionTitle
          icon={Gift}
          title={accountText(lng, "How It Works")}
          description={accountText(
            lng,
            "The referral program is simple: you earn $2, and your friend gets 10% off their first order."
          )}
        />
        <div className="mt-5 grid gap-3">
          <ReferralRule
            icon={UsersRound}
            title={accountText(
              lng,
              "Share your personal referral link with friends"
            )}
            description={accountText(
              lng,
              "Friends get 10% off their first eSIM plan."
            )}
          />
          <ReferralRule
            icon={Gift}
            title={accountText(lng, "$2 per friend")}
            description={accountText(
              lng,
              "After a qualifying purchase, $2 is added to your Yoho account balance for future purchases."
            )}
          />
          <ReferralRule
            icon={ShoppingBag}
            title={accountText(lng, "Rewards are added after a valid purchase")}
            description={accountText(
              lng,
              "Rewards are added after your friend signs up with your code and completes a qualifying purchase."
            )}
          />
        </div>
      </AccountCard>
    </div>
  )
}

function ReferralRule({
  icon,
  title,
  description,
}: {
  icon: LucideIcon
  title: string
  description: string
}) {
  return (
    <div className="flex min-h-14 items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
      <IconBox icon={icon} tone="orange" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-950">{title}</p>
        <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
      </div>
    </div>
  )
}

function MetricCard({
  icon,
  title,
  value,
}: {
  icon: LucideIcon
  title: string
  value: string
}) {
  return (
    <AccountCard className="p-5">
      <div className="flex items-center gap-3">
        <IconBox icon={icon} tone="green" />
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{value}</p>
        </div>
      </div>
    </AccountCard>
  )
}

export function BalanceWebPage({ lng }: LocaleProps) {
  const auth = useAuth()
  const searchParams = useSearchParams()
  const highlightOrder = searchParams.get("highlight") || ""
  const redirectTo = localizeWebAccountPath("/account/balance", lng)

  // The canonical balance lives in Strapi's `customer-extensions` table and
  // is surfaced by /store/get_me as `data[0].balance` (NOT in customer.metadata
  // — `metadata.shopee_balance_topup` is an unsettled Shopee-channel top-up
  // staging value, cleared after the user calls POST /store/shopee_balance_topup
  // which transfers it into the canonical Strapi balance). Confirmed by reading
  // yohomobile-v2 backend (get_me + shopee_balance_topup routes).
  //
  // /api/auth/session pulls `data[0]` and stashes the real numbers on
  // customer.metadata as yoho_balance_* keys so this surface stays untouched
  // by future schema drift.
  const metadata = (auth.customer?.metadata || {}) as AnyRecord
  const balanceRaw = metadata.yoho_balance_minor
  const balanceMinor = Number(balanceRaw)
  const hasBalance = Number.isFinite(balanceMinor) && balanceRaw != null
  const currency =
    (typeof metadata.yoho_balance_currency === "string"
      ? metadata.yoho_balance_currency
      : "") || "USD"
  const balanceFormatted = hasBalance
    ? new Intl.NumberFormat(lng, {
        style: "currency",
        currency: String(currency).toUpperCase(),
      }).format(balanceMinor / 100)
    : ""

  const shouldFetch = auth.status === "authenticated"
  const orders = useEapi(
    shouldFetch ? "store/get_orders?expand=items,items.variant" : null,
    [shouldFetch]
  )

  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "center.balanceTitle")}
      subtitle={accountText(lng, "center.balanceDesc")}
    >
      {auth.status === "loading" ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : auth.status !== "authenticated" ? (
        <LoginRequired
          lng={lng}
          redirectTo={redirectTo}
          title={accountText(lng, "Sign in to see your Yoho balance")}
          description={accountText(lng, "center.balanceDesc")}
          features={[
            {
              icon: Wallet,
              title: accountText(lng, "Account credit"),
              description: accountText(
                lng,
                "Earned from referrals and promotions, applied automatically at checkout."
              ),
            },
          ]}
        />
      ) : (
        <div className="grid gap-5">
          <AccountCard className="p-5 md:p-6">
            <SectionTitle
              icon={Wallet}
              title={accountText(lng, "center.balanceTitle")}
              description={accountText(lng, "center.balanceDesc")}
            />
            <div className="mt-5 rounded-2xl bg-orange-50 p-5 text-orange-900">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-orange-600">
                {accountText(lng, "Account credit")}
              </p>
              <p className="mt-2 text-3xl font-bold tabular-nums">
                {hasBalance
                  ? balanceFormatted
                  : new Intl.NumberFormat(lng, {
                      style: "currency",
                      currency: String(currency).toUpperCase(),
                    }).format(0)}
              </p>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-stone-500">
              {accountText(
                lng,
                "Earned from referrals and promotions, applied automatically at checkout."
              )}
            </p>
          </AccountCard>
          <BalanceTopupHistory
            lng={lng}
            ordersData={orders.data}
            ordersLoading={orders.loading}
            ordersError={orders.error}
            onRetry={orders.reload}
            highlightOrder={highlightOrder}
          />
        </div>
      )}
    </PageShell>
  )
}

function isWalletTopupOrder(order: AnyRecord): boolean {
  const items = normalizeList(order, ["items", "line_items", "order_items"])
  return items.some((item) => {
    const variant = (item as AnyRecord).variant as AnyRecord | undefined
    const meta = (variant?.metadata || {}) as AnyRecord
    const flag = meta.is_wallet_topup
    return flag === true || flag === "true"
  })
}

function BalanceTopupHistory({
  lng,
  ordersData,
  ordersLoading,
  ordersError,
  onRetry,
  highlightOrder,
}: LocaleProps & {
  ordersData: unknown
  ordersLoading: boolean
  ordersError: string | null
  onRetry: () => void
  highlightOrder: string
}) {
  const topupOrders = useMemo(() => {
    const all = normalizeList(ordersData, ["orders", "items", "data"])
    return all.filter(isWalletTopupOrder)
  }, [ordersData])

  const highlightedRowRef = useRef<HTMLLIElement | null>(null)
  const hasHighlightMatch = highlightOrder
    ? topupOrders.some((o) => pickValue(o, ["id", "order_id"]) === highlightOrder)
    : true

  useEffect(() => {
    if (highlightOrder && hasHighlightMatch && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }, [highlightOrder, hasHighlightMatch])

  // Auto-retry once after ~6s when the user just landed from /thankyou with
  // ?highlight= but yoho-task hasn't surfaced the topup order yet.
  useEffect(() => {
    if (!highlightOrder || hasHighlightMatch) return
    const timer = window.setTimeout(() => onRetry(), 6000)
    return () => window.clearTimeout(timer)
  }, [highlightOrder, hasHighlightMatch, onRetry])

  if (ordersLoading && topupOrders.length === 0) {
    return <LoadingCard title={copyText(lng, "loading")} />
  }

  return (
    <AccountCard className="p-5 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle
          icon={ReceiptText}
          title={accountText(lng, "balance.recentTopups")}
          description={accountText(lng, "balance.recentTopupsDesc")}
        />
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 text-xs font-bold text-stone-700 transition hover:border-orange-200 hover:bg-orange-50"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          {accountText(lng, "Refresh")}
        </button>
      </div>

      {highlightOrder && !hasHighlightMatch ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-bold text-amber-900">
            {accountText(lng, "balance.topupPending")}
          </p>
          <p className="mt-1 text-xs leading-5 text-amber-800">
            {accountText(lng, "balance.topupPendingHint")}
          </p>
        </div>
      ) : null}

      {ordersError ? (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {ordersError}
        </div>
      ) : topupOrders.length === 0 ? (
        <p className="mt-4 text-sm leading-6 text-stone-600">
          {accountText(lng, "balance.noTopupsYet")}
        </p>
      ) : (
        <ul className="mt-4 grid gap-2">
          {topupOrders.map((order, index) => {
            const id =
              pickValue(order, ["id", "order_id"]) || `topup-${index}`
            const displayId =
              pickValue(order, ["display_id", "id"]) || ""
            const status = pickValue(order, [
              "status",
              "fulfillment_status",
              "payment_status",
            ])
            const date = formatDate(
              pickValue(order, ["created_at", "createdAt"])
            )
            const rawTotal = pickValue(order, [
              "total",
              "total_amount",
              "amount_total",
              "grand_total",
              "subtotal",
            ])
            const totalDisplay = formatMinorAmount(
              rawTotal,
              pickValue(order, ["currency_code", "currency"]),
              lng
            )
            const isHighlighted = highlightOrder === id

            return (
              <li
                key={id}
                ref={isHighlighted ? highlightedRowRef : undefined}
                className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-3 transition ${
                  isHighlighted
                    ? "border-orange-300 bg-orange-50"
                    : "border-stone-200 bg-white"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-bold text-stone-900">
                    {isFreeAmount(rawTotal)
                      ? accountText(lng, "receipt.free")
                      : totalDisplay
                        ? `+ ${totalDisplay}`
                        : accountText(lng, "receipt.amountUnavailable")}
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500">
                    {date}
                    {displayId ? ` · #${displayId}` : ""}
                  </p>
                </div>
                <StatusBadge status={status} lng={lng} />
              </li>
            )
          })}
        </ul>
      )}
    </AccountCard>
  )
}

export function CouponsWebPage({ lng }: LocaleProps) {
  const auth = useAuth()
  const redirectTo = localizeWebAccountPath("/account/coupons", lng)
  const shouldFetch = auth.status === "authenticated"

  // store/customers/me/coupons IS a real upstream endpoint — Flutter uses it
  // (verified in main.dart.js). I previously removed this fetch on the
  // mistaken assumption it didn't exist; the 401 returned by an unauthenticated
  // probe was actually the proxy guard, not a 404. Restoring the call so
  // logged-in users see their real coupon list. Combine with whatever
  // customer.metadata exposes (some accounts have inline coupons there too).
  const couponList = useEapi(
    shouldFetch ? `store/customers/me/coupons` : null,
    [shouldFetch]
  )
  const metadata = (auth.customer?.metadata || {}) as AnyRecord
  const couponsFromMetadata = (() => {
    for (const key of [
      "coupons",
      "available_coupons",
      "active_coupons",
      "promotions",
    ]) {
      const value = metadata[key]
      if (Array.isArray(value)) return value as AnyRecord[]
    }
    return [] as AnyRecord[]
  })()
  const couponsFromApi: AnyRecord[] = (() => {
    const data = couponList.data
    if (Array.isArray(data)) return data as AnyRecord[]
    if (data && typeof data === "object") {
      for (const key of ["coupons", "data", "items", "list"]) {
        const value = (data as AnyRecord)[key]
        if (Array.isArray(value)) return value as AnyRecord[]
      }
    }
    return []
  })()
  const coupons =
    couponsFromApi.length > 0 ? couponsFromApi : couponsFromMetadata
  const [copiedCouponCode, setCopiedCouponCode] = useState("")
  const copyCouponCode = useCallback(async (couponCode: string) => {
    if (!couponCode) {
      throw new Error("missing_coupon_code")
    }
    await navigator.clipboard.writeText(couponCode)
    setCopiedCouponCode(couponCode)
    window.setTimeout(() => {
      setCopiedCouponCode((current) =>
        current === couponCode ? "" : current
      )
    }, 1600)
  }, [])

  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "center.couponsTitle")}
      subtitle={accountText(lng, "center.couponsDesc")}
    >
      {auth.status === "loading" ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : !shouldFetch ? (
        <LoginRequired
          lng={lng}
          redirectTo={redirectTo}
          title={accountText(lng, "Sign in to see your coupons")}
          description={accountText(lng, "center.couponsDesc")}
          features={[
            {
              icon: TicketPercent,
              title: accountText(lng, "center.couponsTitle"),
              description: accountText(lng, "center.couponsDesc"),
            },
          ]}
        />
      ) : couponList.loading ? (
        <LoadingCard title={copyText(lng, "loading")} />
      ) : coupons.length === 0 ? (
        <AccountCard className="p-5 md:p-7">
          <SectionTitle
            icon={TicketPercent}
            title={accountText(lng, "center.couponsTitle")}
            description={accountText(lng, "center.couponsDesc")}
          />
          <p className="mt-4 text-sm text-stone-500">
            {accountText(lng, "No coupons yet")}
          </p>
        </AccountCard>
      ) : (
        <div className="grid gap-3">
          {coupons.map((coupon, index) => {
            const code = pickValue(coupon, ["code", "coupon_code", "promo_code"])
            const description = pickValue(coupon, [
              "description",
              "label",
              "name",
            ])
            const expiry = pickValue(coupon, [
              "expires_at",
              "expiry",
              "valid_until",
            ])
            const title = code || description || copyText(lng, "empty")
            const copied = Boolean(code) && copiedCouponCode === code
            return (
              <AccountCard
                key={String(code) || index}
                className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-lg font-extrabold tracking-[0.04em] text-stone-950">
                    {title}
                  </p>
                  {description && description !== code ? (
                    <p className="mt-1.5 text-sm leading-5 text-stone-600">
                      {description}
                    </p>
                  ) : null}
                  {expiry ? (
                    <p className="mt-1 text-xs text-stone-500">
                      {accountText(lng, "Expires")}: {String(expiry)}
                    </p>
                  ) : null}
                </div>
                {code ? (
                  <button
                    type="button"
                    onClick={() => void copyCouponCode(code)}
                    className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 text-sm font-extrabold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                  >
                    {copied ? (
                      <Check className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Copy className="h-4 w-4" aria-hidden="true" />
                    )}
                    {copied ? copyText(lng, "copied") : copyText(lng, "copy")}
                  </button>
                ) : null}
              </AccountCard>
            )
          })}
        </div>
      )}
    </PageShell>
  )
}

export function FreeEsimWebPage({ lng }: LocaleProps) {
  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "Free eSIM")}
      subtitle={accountText(lng, "center.freeDesc")}
    >
      <AccountCard className="p-5 md:p-6">
        <SectionTitle
          icon={Smartphone}
          title={accountText(lng, "Free Data in App Only")}
          description={accountText(
            lng,
            "Free Data is only available in the Yoho Mobile App. Download now to claim your free data!"
          )}
        />
        <div className="mt-5 flex flex-wrap gap-3">
          <SecondaryLink href={getAppStoreUrl(lng)}>App Store</SecondaryLink>
          <SecondaryLink href={GOOGLE_PLAY_URL}>Google Play</SecondaryLink>
          <SecondaryLink href={getAppLandingHref(lng)}>
            {accountText(lng, "App page")}
          </SecondaryLink>
        </div>
      </AccountCard>
    </PageShell>
  )
}

export function TravelMapWebPage({ lng }: LocaleProps) {
  return (
    <PageShell
      lng={lng}
      title={accountText(lng, "Travel Map Settings")}
      subtitle={accountText(
        lng,
        "Automatically record visited countries/regions based on eSIM usage"
      )}
    >
      <AccountCard className="p-5 md:p-6">
        <SectionTitle
          icon={MapIcon}
          title={accountText(lng, "How Travel Map Works")}
          description={accountText(
            lng,
            "Your travel map highlights countries where you actually use your Yoho eSIM, not where you purchase plans.\n\nSimply connect to the internet with your eSIM in any country, and it will automatically appear on your map - reflecting your real travel footprint!\n\nNote: This is a beta feature. Visibility of all plans cannot be guaranteed at this time."
          )}
        />
        <div className="mt-5 flex flex-wrap gap-3">
          <SecondaryLink href={localizeWebAccountPath("/buy", lng)}>
            {copyText(lng, "buyEsim")}
          </SecondaryLink>
          <SecondaryLink href={localizeWebAccountPath("/account", lng)}>
            {copyText(lng, "backAccount")}
          </SecondaryLink>
        </div>
      </AccountCard>
    </PageShell>
  )
}

export function AppDownloadSection({ lng }: LocaleProps) {
  return (
    <AccountCard className="p-5 md:p-6">
      <SectionTitle
        icon={Download}
        title={accountText(lng, "Download Yoho App")}
        description={accountText(
          lng,
          "Manage eSIMs, orders, and referrals more easily in the app."
        )}
      />
      <div className="mt-5 flex flex-wrap gap-3">
        <SecondaryLink href={getAppStoreUrl(lng)}>App Store</SecondaryLink>
        <SecondaryLink href={GOOGLE_PLAY_URL}>Google Play</SecondaryLink>
        <SecondaryLink href={getAppLandingHref(lng)}>
          {accountText(lng, "App page")}
        </SecondaryLink>
      </div>
    </AccountCard>
  )
}
